import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { WidgetPanelControls, WidgetPanelLiveStatus } from '../Dashboard/WidgetPanelSlot';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  createChart,
} from 'lightweight-charts';
import { useOptionsStore } from '../../Store/optionsStore';
import useWebSocketDataStore from '../../Store/WebSocketDataStore';
import useGreeksWebsocketDataStore from '../../Store/GreeksWebsocketDataStore';
import usePlaybackStore from '../../Store/playbackStore';
import { useBookmapSettingsStore } from '../../Store/bookmapSettingsStore';
import { acquireBookmapSampler } from '../../Store/bookmapSampler';
import { etDateKey, isDuringMarketHours, latestTradeDayOnly, mostRecentSessionDate, sessionAnchoredSpot, sessionCloseEpochSeconds, sessionOpenEpochSeconds } from '../../Utils/marketHours';
import { aggregateFlowCells, cellValueAt, clampBubbleCenter, minuteFlows, netDeltaTone, netVolByLeg, percentile, selectVolumeDots, STRIKE_WINDOW_PTS, toneClassName, volumeDotGradient } from '../../Utils/strikeFlow';
import { gammaNetExposureSeries, zeroGexCrossingsFromSeries, zeroGexCrossingTimes } from '../../Utils/zeroGex';
import { bucketNormalizedValue } from '../../Utils/scales';
import { tipFollowStyle } from '../../Utils/tooltipPosition';
import { fmtSessionDateET, fmtTooltipPrice0 } from '../../Utils/format';
import { esAdjustedPrice, esAdjustedStrike } from '../../Utils/esAdjustedPrice';
import {
  BOOKMAP_LC_VISUALS,
  BOOKMAP_NET_FLOW_COLORS,
  BOOKMAP_STRIKE_RANGE,
  BOOKMAP_VERTICAL_EASE_MS,
  PRICE_LINE_COLOR,
  PRICE_VISIBLE_WINDOW_PTS,
  clampManualPriceBounds,
  clampPriceBounds,
  expandBoundsToMinStrikes,
  expandOnlyPriceBounds,
  flowWindowBounds,
  lcGridColors,
  meaningfulFlowStrikeBounds,
  mergePriceBounds,
  recenterSpotRange,
  spotStrikeRows,
} from '../../Utils/bookmapVisualSettings';
import { bookmapHeatBandColor, rgbTuple } from '../../Utils/bookmapHeatPalette';
import {
  denseSeriesGapRanges,
  forwardFillSeriesAtGaps,
  mergeSeriesGapRanges,
  rangeContainsTime,
  replaySeriesGapRanges,
  seriesGapRangesForDate,
} from '../../Utils/seriesPresentation';
import { useChartTimeWindowStore } from '../../Store/chartTimeWindowStore';
import { useChartPriceWindowStore } from '../../Store/chartPriceWindowStore';
import useWidgetMinuteFocusStore from '../../Store/widgetMinuteFocusStore';
import {
  DEFAULT_BAR_INTERVAL_SECONDS,
  DEFAULT_VISIBLE_SECONDS,
  MIN_VISIBLE_BARS,
  PLOT_PAN_THRESHOLD_PX,
  RESOLUTION_STATUS,
  RIGHT_GUTTER_BARS,
  WINDOW_MODE,
  clampExtentToSessionClose,
  edgeTakesGutter,
  extentFromEpochSeconds,
  inferBarIntervalSeconds,
  isCustomWindowPreference,
  msFromSeconds,
  resolveWindowPreference,
  rightGutterBarsForContentSpan,
  rightGutterBarsForVisibleSpan,
  sessionDomainSeconds,
} from '../../Utils/chartTimeWindow';
import {
  formatEtChartTime as etChartTime,
  formatEtTime as etTime,
  selectTimeAxisLabels,
} from '../../Utils/chartTimeAxis';
import {
  chooseStrikeLabelStep,
  priceAxisLabelValues,
  selectStrikeLabels,
} from '../../Utils/chartPriceAxis';
import VolumeBubbleThresholdSelect from '../Common/VolumeBubbleThresholdSelect';
import { BookmapMainPrimitive, priceAtChartTime } from './bookmapPrimitives';
import '../../Styles/bookmapPro.css';

const MAX_RENDER_SNAPS = 400;
const MAX_RENDER_DPR = 1.5;
const BOOKMAP_TIME_AXIS_HEIGHT = 24;
const BOOKMAP_STRIKE_GUTTER_WIDTH = 32;
const BOOKMAP_DEFAULT_BAR_SPACING = 6;
// Keep a 24px minimum drag target without consuming more than 12px of plot.
const BOOKMAP_HIST_PRICE_AXIS_HIT_WIDTH = 44;
const BOOKMAP_PRICE_AXIS_DRAG_SENSITIVITY = 2.0;
const WHEEL_LINE_HEIGHT_PX = 16;
const bookmapPlotGutters = new WeakMap();
const hiddenNativePriceScaleCharts = new WeakSet();
// Stable empty reference so the store selector never returns a fresh array - a
// new `[]` each render makes useSyncExternalStore loop ("getSnapshot should be cached").
const EMPTY_BARS = [];


function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function toChartTime(ms) {
  return Math.floor(ms / 60_000) * 60;
}

export function formatWholePrice(value) {
  return Number.isFinite(value) ? Number(value).toFixed(2) : '--';
}

export function flowmapHydrationMessage(
  hydration = {},
  snaps = [],
  latestSessionDate = mostRecentSessionDate(),
) {
  if (!snaps.length) return 'Loading...';

  const latestSnap = snaps[snaps.length - 1];
  const renderedSessionDate = Number.isFinite(latestSnap?.t)
    ? etDateKey(latestSnap.t)
    : null;

  if (
    hydration.source !== 'replay'
    && renderedSessionDate
    && latestSessionDate
    && renderedSessionDate !== latestSessionDate
  ) {
    return 'Loading...';
  }

  if (
    hydration.lifecycle === 'degraded'
    && ['local-sampler', 'authoritative-update-pending-init'].includes(hydration.source)
  ) {
    return 'Current session data is incomplete';
  }

  return hydration.lifecycle === 'loading' ? 'Loading...' : null;
}

export function filterBookmapSessionSnapshots(snaps) {
  return latestTradeDayOnly(
    (snaps || []).filter((snap) => isDuringMarketHours(snap.t))
  );
}

function fmtCompact(value) {
  if (!Number.isFinite(value) || value === 0) return '0';
  const sign = value < 0 ? '-' : '+';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k`;
  return `${sign}${Math.round(abs)}`;
}

export function fmtVolumeAxis(value) {
  if (!Number.isFinite(value) || value === 0) return '0';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const units = [
    { divisor: 1_000_000_000, suffix: 'B' },
    { divisor: 1_000_000, suffix: 'M' },
    { divisor: 1000, suffix: 'k' },
  ];
  let unitIndex = units.findIndex(({ divisor }) => abs >= divisor);
  if (unitIndex < 0) return `${sign}${Math.round(abs)}`;
  let unit = units[unitIndex];
  let scaled = abs / unit.divisor;
  if (scaled >= 999.5 && unitIndex > 0) {
    unit = units[unitIndex - 1];
    scaled = abs / unit.divisor;
  }
  return `${sign}${scaled < 10 ? scaled.toFixed(1) : Math.round(scaled)}${unit.suffix}`;
}

function layerLegendLabel(layer) {
  switch (layer) {
    case 'delta':
      return 'Delta';
    case 'volume':
      return 'Net Vol';
    case 'gamma':
    default:
      return 'Gamma';
  }
}

function volumeAxisTicks(maxAbs) {
  return [maxAbs, maxAbs / 2, 0, -maxAbs / 2, -maxAbs];
}

export function sessionAnchoredVolumeMax(points) {
  const maxAbs = Math.max(
    1,
    ...((points || []).map((point) => Math.abs(point?.value || 0)))
  );
  return maxAbs;
}

export function minuteBucketMs(ms) {
  return Number.isFinite(ms) ? Math.floor(ms / 60000) * 60000 : null;
}

export function includeHighlightedTimeAxisLabel(labels, columns, highlightedTime) {
  if (highlightedTime == null) return labels;
  const highlighted = columns.find((entry) => entry.t === highlightedTime);
  if (!highlighted || labels.some((entry) => entry.t === highlighted.t)) return labels;
  return labels
    .filter((entry) => Math.abs(entry.x - highlighted.x) >= 44)
    .concat(highlighted)
    .sort((a, b) => a.x - b.x);
}

function anchorLogicalRangeAt(range, previous, anchor, lowerBound = -Infinity) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  const previousFrom = Number(previous?.from);
  const previousTo = Number(previous?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return null;

  const floor = Number.isFinite(lowerBound) ? lowerBound : -Infinity;
  if (
    !Number.isFinite(anchor)
    || !Number.isFinite(previousFrom)
    || !Number.isFinite(previousTo)
    || !(previousTo > previousFrom)
    || anchor < previousFrom
    || anchor > previousTo
  ) {
    return { from, to };
  }

  const rightFraction = (previousTo - anchor) / (previousTo - previousFrom);
  const leftFraction = 1 - rightFraction;
  const requestedSpan = to - from;
  const maxSpan = leftFraction > 0
    ? (anchor - floor) / leftFraction
    : Infinity;
  const span = Math.min(requestedSpan, maxSpan);
  return {
    from: anchor - span * leftFraction,
    to: anchor + span * rightFraction,
  };
}

export function scaleTimeAxisRange(range, deltaX, plotWidth, limits = null) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return null;
  const width = Math.max(80, Number(plotWidth) || 0);
  const sessionOpen = Number(limits?.sessionOpen);
  const sessionClose = Number(limits?.sessionClose);
  const span = Math.max(2, (to - from) * Math.exp(deltaX / width));
  const lowerBound = Number.isFinite(sessionOpen) ? Math.min(from, sessionOpen) : -Infinity;
  if (!Number.isFinite(sessionClose) || sessionClose < from || sessionClose > to) {
    return { from: Math.max(lowerBound, to - span), to };
  }
  return anchorLogicalRangeAt(
    { from: to - span, to },
    { from, to },
    sessionClose,
    lowerBound
  );
}

export function scalePriceAxisRange(range, deltaY, plotHeight) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return null;

  const height = Math.max(80, Number(plotHeight) || 0);
  const midpoint = (from + to) / 2;
  const currentSpan = to - from;
  const scaleFactor = Math.exp(
    (Number(deltaY) / height) * BOOKMAP_PRICE_AXIS_DRAG_SENSITIVITY
  );
  const nextSpan = Math.max(2, currentSpan * scaleFactor);

  return {
    from: midpoint - nextSpan / 2,
    to: midpoint + nextSpan / 2,
  };
}

export function normalizeWheelDelta(event, pageSizePx) {
  const delta = Number(event?.deltaY);
  if (!Number.isFinite(delta)) return 0;
  if (event?.deltaMode === 1) return delta * WHEEL_LINE_HEIGHT_PX;
  if (event?.deltaMode === 2) return delta * Math.max(1, Number(pageSizePx) || 0);
  return delta;
}

export function translatePriceRange(range, deltaY, plotHeight, limits = null) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return null;

  const height = Math.max(80, Number(plotHeight) || 0);
  const span = to - from;
  const shift = span * (Number(deltaY) / height);
  let next = { from: from + shift, to: to + shift };
  const floor = Number(limits?.from);
  const ceiling = Number(limits?.to);
  if (!Number.isFinite(floor) || !Number.isFinite(ceiling) || !(ceiling > floor)) {
    return next;
  }
  if (span >= ceiling - floor) return next;
  if (next.from < floor) next = { from: floor, to: floor + span };
  if (next.to > ceiling) next = { from: ceiling - span, to: ceiling };
  return next;
}

function currentVisiblePriceRange(chart, priceSeries, plotHeight) {
  if (!chart || !priceSeries) return null;

  const apiRange = chart.priceScale('left').getVisibleRange?.();
  if (
    Number.isFinite(apiRange?.from)
    && Number.isFinite(apiRange?.to)
    && apiRange.to > apiRange.from
  ) {
    return { from: apiRange.from, to: apiRange.to };
  }

  const height = Math.max(1, Number(plotHeight) || 0);
  const topPrice = priceSeries.coordinateToPrice?.(0);
  const bottomPrice = priceSeries.coordinateToPrice?.(height);
  if (!Number.isFinite(topPrice) || !Number.isFinite(bottomPrice)) return null;

  return {
    from: Math.min(topPrice, bottomPrice),
    to: Math.max(topPrice, bottomPrice),
  };
}

export function visibleStrikeRowsForPriceRange(
  snaps,
  range,
  fallbackSpot = null,
  config = BOOKMAP_STRIKE_RANGE
) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return [];

  const ceiling = manualPriceCeiling(snaps, fallbackSpot);
  const visibleBounds = ceiling
    ? {
        from: Math.max(from, ceiling.from),
        to: Math.min(to, ceiling.to),
      }
    : { from, to };

  if (!(visibleBounds.to >= visibleBounds.from)) return [];

  const candidateRows = strikeRowsFromBounds(visibleBounds, fallbackSpot, config);
  if (!ceiling) return candidateRows;

  // strikeRowsFromBounds intentionally rounds outward to the strike grid. Clamp
  // that overscan back to the actual hydrated universe so manual Y expansion
  // never invents rows the snapshot feed did not supply.
  return candidateRows.filter((strike) => (
    strike >= ceiling.from
    && strike <= ceiling.to
  ));
}

function netToneClass(value) {
  return toneClassName(netDeltaTone(value), 'bmpro-green', 'bmpro-red');
}

function useElementSize(ref, fallback) {
  const [size, setSize] = useState(fallback);

  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(1, Math.round(width)), h: Math.max(1, Math.round(height)) });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}

export function plotGeometry(chart, width, height) {
  const left = bookmapPlotGutters.get(chart) ?? (chart?.priceScale?.('left')?.width?.() || 0);
  const rightScale = chart?.priceScale?.('right')?.width?.() || 0;
  const requestedPane = width - left - rightScale;
  const measuredPane = chart?.timeScale?.()?.width?.();
  // Library pane is the real plot edge (upper bound). Caller size is the
  // fallback when the scale is not measurable yet — not a second source of
  // truth that can drift from applyOptions.
  const paneWidth = Number.isFinite(measuredPane) && measuredPane > 0
    ? Math.min(measuredPane, requestedPane)
    : requestedPane;
  // Callers pass the full Bookmap tile height; the shared overlay owns this band.
  return {
    left,
    right: Math.min(width - rightScale, left + paneWidth),
    top: 0,
    bottom: Math.max(1, height - BOOKMAP_TIME_AXIS_HEIGHT),
  };
}

function scheduleViewportRefresh(rafRef, setViewportVersion) {
  if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
  rafRef.current = window.requestAnimationFrame(() => {
    rafRef.current = null;
    setViewportVersion((value) => value + 1);
  });
}

/**
 * Refresh after lightweight-charts has laid out a range we just set.
 *
 * `setVisibleRange`/`setVisibleLogicalRange` apply on the library's own frame,
 * so a single rAF can still read the pre-apply time mapping. The external
 * overlays draw through `timeToCoordinate`, and nothing repaints them once the
 * library settles -- their effects key off data and size -- so losing that race
 * leaves a canvas drawn against the superseded range until the next snapshot
 * lands, ~15s later. Interactive paths keep the single-frame refresh; only
 * programmatic range changes pay the extra frame.
 */
export function scheduleSettledViewportRefresh(rafRef, setViewportVersion) {
  if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
  rafRef.current = window.requestAnimationFrame(() => {
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      setViewportVersion((value) => value + 1);
    });
  });
}

function rowHalfStep(rows) {
  if (rows.length < 2) return null;
  const gaps = [];
  for (let index = 1; index < rows.length; index += 1) {
    const gap = rows[index] - rows[index - 1];
    if (gap > 0) gaps.push(gap);
  }
  return Math.max(1, (percentile(gaps, 0.5) || 5) / 2);
}

export function rowEdgeRange(rows, bounds = null) {
  if (!rows.length) return null;
  const halfStep = rowHalfStep(rows);
  if (!halfStep) return null;
  const from = Number.isFinite(bounds?.from) ? bounds.from : rows[0];
  const to = Number.isFinite(bounds?.to) ? bounds.to : rows[rows.length - 1];
  return {
    from: from - halfStep,
    to: to + halfStep,
  };
}

function visibleSnapsInPlot(snaps, chart, width, height) {
  if (!snaps.length || !chart) return [];
  const plot = plotGeometry(chart, width, height);
  return snaps.filter((snap) => {
    const chartX = chart.timeScale().timeToCoordinate(toChartTime(snap.t));
    const x = Number.isFinite(chartX) ? plot.left + chartX : chartX;
    return Number.isFinite(x) && x >= plot.left && x <= plot.right;
  });
}

export function hydratedStrikeBounds(snaps, windowPts = STRIKE_WINDOW_PTS) {
  let min = Infinity;
  let max = -Infinity;

  for (const snap of snaps) {
    if (Number.isFinite(snap?.hydrationFrom) && Number.isFinite(snap?.hydrationTo)) {
      min = Math.min(min, snap.hydrationFrom);
      max = Math.max(max, snap.hydrationTo);
      continue;
    }
    const scopedSpot = Number(snap?.spot);
    if (Number.isFinite(scopedSpot) && Number.isFinite(windowPts)) {
      min = Math.min(min, scopedSpot - windowPts);
      max = Math.max(max, scopedSpot + windowPts);
      continue;
    }
    for (const strikeKey of Object.keys(snap?.byStrike || {})) {
      const strike = Number(strikeKey);
      if (!Number.isFinite(strike)) continue;
      min = Math.min(min, strike);
      max = Math.max(max, strike);
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { from: min, to: max };
}

function manualPriceCeiling(snaps, fallbackSpot) {
  return hydratedStrikeBounds(snaps)
    ?? flowWindowBounds(null, fallbackSpot, STRIKE_WINDOW_PTS);
}

export function hydratedStrikeRows(snaps, lineData = [], fallbackSpot = null, config = BOOKMAP_STRIKE_RANGE) {
  let min = Infinity;
  let max = -Infinity;

  const hydratedBounds = hydratedStrikeBounds(snaps);
  if (hydratedBounds) {
    min = hydratedBounds.from;
    max = hydratedBounds.to;
  }

  for (const point of lineData) {
    const value = Number(point?.value);
    const low = Number(point?.low);
    const high = Number(point?.high);
    if (Number.isFinite(low) && Number.isFinite(high)) {
      min = Math.min(min, low);
      max = Math.max(max, high);
      continue;
    }
    if (!Number.isFinite(value)) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return spotStrikeRows(fallbackSpot, config);
  }

  return strikeRowsFromBounds({ from: min, to: max }, fallbackSpot, config);
}

function strikeRowsFromBounds(bounds, fallbackSpot = null, config = BOOKMAP_STRIKE_RANGE) {
  const step = config.step || 5;
  if (!Number.isFinite(bounds?.from) || !Number.isFinite(bounds?.to)) {
    return spotStrikeRows(fallbackSpot, config);
  }
  const from = Math.floor(bounds.from / step) * step;
  const to = Math.ceil(bounds.to / step) * step;
  const rows = [];
  for (let strike = from; strike <= to; strike += step) rows.push(strike);
  return rows;
}

function sameStrikeRows(a = [], b = []) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

export function sameNumericRange(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.from === b.from && a.to === b.to;
}

const lastLockedRangeByChart = new WeakMap();

function padBounds(bounds, pad) {
  if (!Number.isFinite(bounds?.from) || !Number.isFinite(bounds?.to)) return null;
  return { from: bounds.from - pad, to: bounds.to + pad };
}

function visiblePriceLineBounds(lineData, chart, width, height) {
  if (!lineData?.length || !chart) return null;
  const plot = plotGeometry(chart, width, height);
  const points = lineData
    .map((point) => {
      const chartX = chart.timeScale().timeToCoordinate(point.time);
      const value = Number(point?.value);
      const low = Number(point?.low);
      const high = Number(point?.high);
      return {
        value,
        low: Number.isFinite(low) ? low : value,
        high: Number.isFinite(high) ? high : value,
        x: Number.isFinite(chartX) ? plot.left + chartX : chartX,
      };
    })
    .filter((point) => (
      Number.isFinite(point.low)
      && Number.isFinite(point.high)
      && Number.isFinite(point.x)
    ));

  if (!points.length) return null;

  let min = Infinity;
  let max = -Infinity;
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    if (Math.max(a.x, b.x) < plot.left || Math.min(a.x, b.x) > plot.right) continue;
    min = Math.min(min, a.low, b.low);
    max = Math.max(max, a.high, b.high);
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    for (const point of points) {
      if (point.x < plot.left || point.x > plot.right) continue;
      min = Math.min(min, point.low);
      max = Math.max(max, point.high);
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { from: min, to: max };
}

function priceLineBounds(lineData) {
  let from = Infinity;
  let to = -Infinity;
  for (const point of lineData || []) {
    const value = Number(point?.value);
    const low = Number(point?.low);
    const high = Number(point?.high);
    if (Number.isFinite(low) && Number.isFinite(high)) {
      from = Math.min(from, low);
      to = Math.max(to, high);
    } else if (Number.isFinite(value)) {
      from = Math.min(from, value);
      to = Math.max(to, value);
    }
  }
  return Number.isFinite(from) && Number.isFinite(to) ? { from, to } : null;
}

function lockPriceScaleToRows(chart, rows, rafRef, setViewportVersion, bounds = null) {
  const range = rowEdgeRange(rows, bounds);
  if (!chart || !range) return false;
  if (sameNumericRange(lastLockedRangeByChart.get(chart), range)) return false;
  lastLockedRangeByChart.set(chart, range);
  const priceScale = chart.priceScale('left');
  priceScale.setAutoScale(false);
  priceScale.setVisibleRange(range);
  if (hiddenNativePriceScaleCharts.has(chart)) {
    // A hidden Lightweight Charts scale discards its explicit range. Keep the
    // write for Fit View, then let the invisible boundary series own the same
    // range so all strike rows remain projected into the custom gutter.
    priceScale.setAutoScale(true);
  }
  scheduleViewportRefresh(rafRef, setViewportVersion);
  return true;
}

// `applyAutoFit` runs from `handleRange`, so this scan repeats every frame of a
// zoom, and in Session mode the scope is the whole day: ~390 snapshots times
// every hydrated strike. The result only changes when the snapshot array or the
// active layer does.
const flowBoundsCache = new WeakMap();

function cachedFlowStrikeBounds(sourceSnaps, scopedSnaps, layer) {
  if (!sourceSnaps?.length || !scopedSnaps?.length) return null;
  let byScope = flowBoundsCache.get(sourceSnaps);
  if (!byScope) {
    byScope = new Map();
    flowBoundsCache.set(sourceSnaps, byScope);
  }
  const firstTime = Number(scopedSnaps[0]?.t);
  const lastTime = Number(scopedSnaps[scopedSnaps.length - 1]?.t);
  const scopeKey = `${layer}:${firstTime}:${lastTime}:${scopedSnaps.length}`;
  if (byScope.has(scopeKey)) return byScope.get(scopeKey);
  const bounds = meaningfulFlowStrikeBounds(
    scopedSnaps,
    (snap, strike) => bucketNormalizedValue(cellValueAt(snap, strike, layer), layer) !== 0
  );
  byScope.set(scopeKey, bounds);
  return bounds;
}

function computeVisibleFit(
  chart,
  snaps,
  lineData,
  fallbackSpot,
  width,
  height,
  layer,
  sessionMode = false
) {
  if (!chart) return { rows: [], bounds: null, flowCeiling: null, scaleCeiling: null };
  const scopedSnaps = sessionMode
    ? snaps
    : visibleSnapsInPlot(snaps, chart, width, height);
  const hydratedBounds = hydratedStrikeBounds(scopedSnaps);
  const flowCeiling = flowWindowBounds(
    hydratedBounds,
    fallbackSpot,
    STRIKE_WINDOW_PTS
  );
  const priceBounds = padBounds(
    sessionMode
      ? priceLineBounds(lineData)
      : visiblePriceLineBounds(lineData, chart, width, height),
    PRICE_VISIBLE_WINDOW_PTS
  );
  const flowBounds = clampPriceBounds(
    padBounds(
      cachedFlowStrikeBounds(snaps, scopedSnaps, layer),
      PRICE_VISIBLE_WINDOW_PTS
    ),
    flowCeiling
  );
  const fallbackBounds = clampPriceBounds(
    expandBoundsToMinStrikes(hydratedBounds),
    flowCeiling
  );
  const fittedBounds = expandBoundsToMinStrikes(
    mergePriceBounds(priceBounds, flowBounds) || fallbackBounds
  );
  const scaleCeiling = mergePriceBounds(
    expandBoundsToMinStrikes(priceBounds),
    flowCeiling
  );
  const bounds = clampPriceBounds(fittedBounds, scaleCeiling);
  const rowBounds = clampPriceBounds(bounds, flowCeiling);
  return {
    rows: strikeRowsFromBounds(rowBounds, fallbackSpot),
    bounds,
    flowCeiling,
    scaleCeiling,
  };
}

function syncFitRows(setRows, fitRows) {
  if (typeof setRows === 'function') {
    setRows((prev) => (sameStrikeRows(prev, fitRows) ? prev : fitRows));
  }
}

function boundarySeriesFromRows(lineData, rows, bounds = null) {
  const range = rowEdgeRange(rows, bounds);
  if (!range || !lineData.length) return { upper: [], lower: [] };
  return {
    upper: lineData.map((point) => ({ time: point.time, value: range.to })),
    lower: lineData.map((point) => ({ time: point.time, value: range.from })),
  };
}

export function syncBoundarySeries(
  cacheRef,
  mainApi,
  lineData,
  rows,
  bounds = null,
  { skipUnchanged = false } = {}
) {
  const range = rowEdgeRange(rows, bounds);
  if (!mainApi) return false;
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const applied = cacheRef.current;
  const unchanged = Boolean(
    applied
    && applied.upperSeries === mainApi.upperSeries
    && applied.lineData === lineData
    && applied.from === from
    && applied.to === to
  );
  if (skipUnchanged && unchanged) return false;

  const boundary = boundarySeriesFromRows(lineData, rows, bounds);
  mainApi.upperSeries.setData(boundary.upper);
  mainApi.lowerSeries.setData(boundary.lower);
  cacheRef.current = {
    upperSeries: mainApi.upperSeries,
    lineData,
    from,
    to,
  };
  return true;
}

function verticalFitScopeKey(replaying, sessionDate, mode, layer) {
  return `${replaying ? 'replay' : 'live'}:${sessionDate}:${mode}:${layer}`;
}

function fitPriceScaleToVisibleData(
  chart,
  snaps,
  lineData,
  fallbackSpot,
  setRows,
  width,
  height,
  rafRef,
  setViewportVersion,
  layer,
  previousBounds = null,
  sessionMode = false
) {
  const initial = computeVisibleFit(
    chart,
    snaps,
    lineData,
    fallbackSpot,
    width,
    height,
    layer,
    sessionMode
  );
  const bounds = clampPriceBounds(
    expandOnlyPriceBounds(previousBounds, initial.bounds),
    initial.scaleCeiling
  );
  const rowBounds = clampPriceBounds(bounds, initial.flowCeiling);
  const computed = {
    bounds,
    rows: rowBounds ? strikeRowsFromBounds(rowBounds, fallbackSpot) : initial.rows,
  };
  syncFitRows(setRows, computed.rows);
  if (!computed.rows.length) return { rows: computed.rows, bounds: computed.bounds, scaleChanged: false };
  return {
    rows: computed.rows,
    bounds: computed.bounds,
    scaleChanged: lockPriceScaleToRows(
      chart,
      computed.rows,
      rafRef,
      setViewportVersion,
      computed.bounds
    ),
  };
}

/**
 * Timestamp to logical index over the price series.
 *
 * Lightweight Charts resolves a timestamp to an index with `findNearest`, so any
 * time past the final point collapses onto the last index. `setVisibleRange`
 * therefore silently discarded both the right gutter and the reserved part of
 * the session domain, `getVisibleRange` could never report them back, and
 * `timeToCoordinate` returned null inside them -- the reservation and the gutter
 * were inert on this widget while the price chart, whose library scrolls past
 * the end natively, showed both. Logical space has no such ceiling: indices past
 * the data are ordinary whitespace. Every range this widget applies, reads back,
 * or labels goes through this projection instead.
 *
 * Interior times interpolate between their bracketing columns; beyond either end
 * the projection extrapolates at the inferred bar interval, which is exact for
 * the reserved region because it holds no columns to disagree with.
 */
function timeToLogical(lineData, barIntervalSeconds, time) {
  const lastIndex = lineData.length - 1;
  const firstTime = lineData[0].time;
  const lastTime = lineData[lastIndex].time;
  if (time <= firstTime) return (time - firstTime) / barIntervalSeconds;
  if (time >= lastTime) return lastIndex + (time - lastTime) / barIntervalSeconds;

  let low = 0;
  let high = lastIndex;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (lineData[mid].time <= time) low = mid;
    else high = mid;
  }
  const span = lineData[high].time - lineData[low].time;
  return span > 0 ? low + (time - lineData[low].time) / span : low;
}

export function lastDataLogicalIndex(scaleData, lineData, barIntervalSeconds) {
  const dataLastTime = lineData[lineData.length - 1]?.time;
  return scaleData.length && Number.isFinite(dataLastTime)
    ? timeToLogical(scaleData, barIntervalSeconds, dataLastTime)
    : null;
}

function logicalToTime(lineData, barIntervalSeconds, logical) {
  const lastIndex = lineData.length - 1;
  if (logical <= 0) return lineData[0].time + logical * barIntervalSeconds;
  if (logical >= lastIndex) {
    return lineData[lastIndex].time + (logical - lastIndex) * barIntervalSeconds;
  }
  const low = Math.floor(logical);
  const high = Math.min(lastIndex, low + 1);
  return lineData[low].time + (lineData[high].time - lineData[low].time) * (logical - low);
}

function sessionCloseLogical(scaleData) {
  if (!scaleData?.length) return null;
  const close = sessionCloseEpochSeconds(scaleData[0].time * 1000);
  return timeToLogical(scaleData, DEFAULT_BAR_INTERVAL_SECONDS, close);
}

function sessionLogicalSpan(scaleData) {
  if (!scaleData?.length) return null;
  const open = sessionOpenEpochSeconds(scaleData[0].time * 1000);
  const openLogical = timeToLogical(scaleData, DEFAULT_BAR_INTERVAL_SECONDS, open);
  const closeLogical = sessionCloseLogical(scaleData);
  const span = closeLogical - openLogical;
  return Number.isFinite(span) && span > 0 ? span : null;
}

const gutterForContentSpan = (scaleData, contentSpan) => rightGutterBarsForContentSpan(
  contentSpan,
  sessionLogicalSpan(scaleData)
);

const gutterForVisibleSpan = (scaleData, visibleSpan) => rightGutterBarsForVisibleSpan(
  visibleSpan,
  sessionLogicalSpan(scaleData)
);

/**
 * Hold a logical range inside the session, between open index 0 and `maxTo`.
 *
 * `maxTo` is the session close plus the blank gutter -- the same ceiling
 * `maxRightOffsetBars` expresses in bar widths for the price chart. Without it a
 * repeated drag walks the right edge hours past 16:00, which is the mirror of
 * the premarket overshoot on the left.
 *
 * `previous` is the range the chart last displayed, and it decides which
 * correction applies. A scroll moves the range at constant bar spacing, so an
 * unchanged span means the gesture is a pan: trimming the overshot edge would
 * shorten the window on every frame of a drag and walk the chart into a zoom,
 * so the span slides against the edge instead. A changed span is a real zoom
 * and keeps the trim, which is what stops a zoom-out from overrunning the
 * session. A range wholly outside the session has no part left to trim to, so
 * it slides whatever the gesture was.
 *
 * Callers that move one edge on purpose -- the time-axis drag, and an explicit
 * interval through `applyLogicalTimeRange` -- pass neither option and trim.
 */
export function clampVisibleLogicalRange(
  range,
  { previous = null, maxTo = Infinity, maxPanTo = maxTo } = {}
) {
  const from = Number(range?.from);
  const to = Number(range?.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;

  const span = to - from;
  const previousSpan = Number(previous?.to) - Number(previous?.from);
  const isPan = Number.isFinite(previousSpan) && Math.abs(previousSpan - span) < 1e-6;
  const ceiling = isPan ? Math.min(maxTo, maxPanTo) : maxTo;
  if (from >= 0 && to <= ceiling) return { from, to };

  if (isPan || to <= 0 || from >= ceiling) {
    const width = Math.min(span, ceiling);
    const start = Math.min(Math.max(0, from), ceiling - width);
    return { from: start, to: start + width };
  }
  return { from: Math.max(0, from), to: Math.min(ceiling, to) };
}

/**
 * The furthest right an interactive candidate may push the visible range.
 *
 * The session domain runs to 16:00, so bounding a drag at the close alone still
 * lets the map be pulled off to the left until nothing but empty grid faces the
 * strike histogram. A pan stops at the last bar plus the blank gutter. A zoom
 * can retain the session-close anchor while it changes the span and hides
 * earlier minutes. A session-spanning candidate can also use the full-day grid.
 */
export function panRightCeiling(
  scaleData,
  lastDataTime,
  visibleSpan = null,
  previousSpan = null,
  zoomIntent = false
) {
  if (!scaleData?.length) return Infinity;
  const sessionClose = sessionCloseLogical(scaleData);
  const gutter = Math.min(RIGHT_GUTTER_BARS, gutterForVisibleSpan(scaleData, visibleSpan));
  const sessionCeiling = Number.isFinite(sessionClose)
    ? sessionClose + gutter
    : Infinity;
  if (!Number.isFinite(lastDataTime)) return sessionCeiling;
  const dataCeiling = timeToLogical(scaleData, DEFAULT_BAR_INTERVAL_SECONDS, lastDataTime)
    + gutter;
  const sessionSpan = sessionLogicalSpan(scaleData);
  const spansSession = Number.isFinite(visibleSpan)
    && Number.isFinite(sessionSpan)
    && visibleSpan >= sessionSpan - 1.5;
  const isZoom = zoomIntent || (Number.isFinite(visibleSpan)
    && Number.isFinite(previousSpan)
    && Math.abs(visibleSpan - previousSpan) >= 1);
  return Math.min(sessionCeiling, spansSession || isZoom ? sessionCeiling : dataCeiling);
}

function buildSessionTimeScaleData(anchorTime) {
  if (!Number.isFinite(anchorTime)) return [];
  // Streaming candles contain only observed minutes. This complete session grid
  // is the projection source for candle gaps and reserved future minutes.
  const first = sessionOpenEpochSeconds(anchorTime * 1000);
  const last = sessionCloseEpochSeconds(anchorTime * 1000);
  const data = [];
  // The close is an axis boundary, not a minute that can contain data.
  for (let time = first; time < last; time += DEFAULT_BAR_INTERVAL_SECONDS) {
    data.push({ time, value: 0 });
  }
  return data;
}

/**
 * Apply a timestamp range in logical space.
 *
 * The gutter is blank room so a bubble centred on the final column draws whole,
 * so it belongs only at an anchored edge -- see `edgeTakesGutter`. Padding an
 * interior range instead widens it by ten real bars: a requested 14:30-15:00
 * would render through 15:10 and stop being the interval that was asked for. It
 * is never part of a stored or published range either way.
 */
export function applyLogicalTimeRange(
  chart,
  timeScaleData,
  sourceBarIntervalSeconds,
  range,
  dataLastTime = timeScaleData[timeScaleData.length - 1].time
) {
  // Index 0 is the session-open column of `timeScaleData`. A negative `from`
  // is what `logicalToTime` extrapolates into premarket, including the 07:30
  // label from a requested range two hours before the open. The source candle
  // interval affects edge classification only; this grid is always one minute.
  const from = timeToLogical(timeScaleData, DEFAULT_BAR_INTERVAL_SECONDS, range.from);
  const edgeTo = timeToLogical(timeScaleData, DEFAULT_BAR_INTERVAL_SECONDS, range.to);
  const gutter = edgeTakesGutter(range.to, dataLastTime, sourceBarIntervalSeconds)
    ? gutterForContentSpan(timeScaleData, edgeTo - Math.max(0, from))
    : 0;
  const to = edgeTo + gutter;
  // The native width/2 bar-spacing ceiling needs two logical bars. Reserve a
  // missing bar to the right so the scale does not fill left into premarket.
  // Only when the window starts at the open, which is the only place with
  // premarket to the left to expand into. An interior range the user asked for
  // is left exactly as requested.
  const floored = from <= 0 && to - from < MIN_VISIBLE_BARS
    ? { from, to: from + MIN_VISIBLE_BARS }
    : { from, to };
  const visible = clampVisibleLogicalRange(floored);
  if (!visible) return false;
  chart.timeScale().setVisibleLogicalRange(visible);
  return true;
}

export function applyDefaultRollingTimeRange(
  chart,
  lineData,
  seconds = DEFAULT_VISIBLE_SECONDS,
  timeScaleData = lineData
) {
  if (!chart || !lineData?.length) return null;
  const lastIndex = lineData.length - 1;
  const lastTime = lineData[lastIndex].time;
  const firstTime = lastTime - seconds;
  let firstIndex = 0;
  for (let index = lastIndex; index >= 0; index -= 1) {
    if (lineData[index].time < firstTime) {
      firstIndex = Math.min(lastIndex, index + 1);
      break;
    }
  }
  const from = lineData[firstIndex].time;
  if (!Number.isFinite(from)) return null;
  // No `from >= lastTime` bail: a single loaded bar has no span of its own, and
  // returning null here left whatever range preceded it on screen. The minimum
  // window in `applyLogicalTimeRange` gives it one, anchored at the open.
  const range = { from, to: lastTime };
  try {
    const applied = applyLogicalTimeRange(
      chart,
      timeScaleData,
      inferBarIntervalSeconds(lineData.map((point) => point.time)),
      range,
      lastTime
    );
    if (!applied) return null;
  } catch {
    // The chart can be recreated before its series has accepted the new points.
    return null;
  }
  // Returned rather than read back: lightweight-charts applies the range
  // asynchronously, so getVisibleRange() right after this still reports the
  // previous view.
  return range;
}

export function buildHeatCells(snaps, rows, layer, palette, chart, priceSeries, width, height) {
  if (!snaps.length || !rows.length || !chart || !priceSeries) return [];
  const plot = plotGeometry(chart, width, height);
  const rowEntries = rows
    .slice()
    .reverse()
    .map((strike) => ({
      strike,
      y: priceSeries.priceToCoordinate(strike),
    }))
    .filter((entry) => Number.isFinite(entry.y));
  const colEntries = snaps
    .map((snap) => {
      const x = chart.timeScale().timeToCoordinate(toChartTime(snap.t));
      return { snap, x: Number.isFinite(x) ? plot.left + x : x };
    })
    .filter((entry) => Number.isFinite(entry.x));
  if (rowEntries.length < 2 || colEntries.length < 2) return [];

  const rowBounds = rowEntries.map((entry, index) => {
    const prev = rowEntries[index - 1];
    const next = rowEntries[index + 1];
    const top = index === 0 ? entry.y - ((next.y - entry.y) / 2) : (prev.y + entry.y) / 2;
    const bottom = index === rowEntries.length - 1 ? entry.y + ((entry.y - prev.y) / 2) : (entry.y + next.y) / 2;
    return { ...entry, top, bottom };
  });
  const colBounds = colEntries.map((entry, index) => {
    const prev = colEntries[index - 1];
    const next = colEntries[index + 1];
    const minuteAgo = chart.timeScale().timeToCoordinate(toChartTime(entry.snap.t) - 60);
    const minuteAhead = chart.timeScale().timeToCoordinate(toChartTime(entry.snap.t) + 60);
    const oneMinuteLeft = Number.isFinite(minuteAgo)
      ? (entry.x + plot.left + minuteAgo) / 2
      : null;
    const oneMinuteRight = Number.isFinite(minuteAhead)
      ? (entry.x + plot.left + minuteAhead) / 2
      : null;
    const neighborLeft = prev
      ? (prev.x + entry.x) / 2
      : (next ? entry.x - (next.x - entry.x) / 2 : entry.x - 4);
    const neighborRight = next
      ? (entry.x + next.x) / 2
      : (prev ? entry.x + (entry.x - prev.x) / 2 : entry.x + 4);
    const left = Number.isFinite(oneMinuteLeft)
      ? Math.max(neighborLeft, oneMinuteLeft)
      : neighborLeft;
    const right = Number.isFinite(oneMinuteRight)
      ? Math.min(neighborRight, oneMinuteRight)
      : neighborRight;
    return { ...entry, left, right };
  });

  const cells = [];
  for (const col of colBounds) {
    for (const row of rowBounds) {
      const raw = cellValueAt(col.snap, row.strike, layer);
      if (!raw) continue;
      const norm = bucketNormalizedValue(raw, layer);
      const { rgb, alpha } = bookmapHeatBandColor(norm, palette);
      const left = clamp(col.left, plot.left, plot.right);
      const right = clamp(col.right, plot.left, plot.right);
      const top = clamp(row.top, plot.top, plot.bottom);
      const bottom = clamp(row.bottom, plot.top, plot.bottom);
      if (right <= left || bottom <= top) continue;
      cells.push({
        left,
        top,
        width: right - left,
        height: bottom - top,
        color: rgbTuple(rgb, alpha),
      });
    }
  }
  return cells;
}

function buildTimeColumns(snaps, chart, width, height) {
  if (!snaps.length || !chart) return [];
  const plot = plotGeometry(chart, width, height);
  const colEntries = snaps
    .map((snap) => {
      const x = chart.timeScale().timeToCoordinate(toChartTime(snap.t));
      return { t: snap.t, x: Number.isFinite(x) ? plot.left + x : x };
    })
    .filter((entry) => Number.isFinite(entry.x));
  if (!colEntries.length) return [];

  return colEntries.map((entry, index) => {
    const prev = colEntries[index - 1];
    const next = colEntries[index + 1];
    const minuteAgo = chart.timeScale().timeToCoordinate(toChartTime(entry.t) - 60);
    const minuteAhead = chart.timeScale().timeToCoordinate(toChartTime(entry.t) + 60);
    const oneMinuteLeft = Number.isFinite(minuteAgo) ? plot.left + minuteAgo : null;
    const oneMinuteRight = Number.isFinite(minuteAhead) ? plot.left + minuteAhead : null;
    const neighborLeft = index === 0
      ? entry.x - ((next?.x ?? entry.x + 8) - entry.x) / 2
      : (prev.x + entry.x) / 2;
    const neighborRight = index === colEntries.length - 1
      ? entry.x + (entry.x - (prev?.x ?? entry.x - 8)) / 2
      : (entry.x + next.x) / 2;
    const left = Number.isFinite(oneMinuteLeft)
      ? Math.max(neighborLeft, oneMinuteLeft)
      : neighborLeft;
    const right = Number.isFinite(oneMinuteRight)
      ? Math.min(neighborRight, oneMinuteRight)
      : neighborRight;
    return {
      ...entry,
      // Left/right are clamped into the plot so cells stay inside it, which
      // hides whether the column itself is actually on screen. Callers that
      // label a column need to know: a snapshot after the visible range still
      // projects to a clamped edge position while carrying its own later time.
      offPlot: entry.x < plot.left || entry.x > plot.right,
      rawLeft: left,
      rawRight: right,
      left: clamp(left, plot.left, plot.right),
      right: clamp(right, plot.left, plot.right),
    };
  }).filter((entry) => entry.right > plot.left && entry.left < plot.right);
}

// Shared by the bar and its hover highlight so the two cannot disagree about how
// wide a column is. Use the unclipped span so an edge column keeps the same bar
// width as an interior column.
export function volumeColumnBarWidth(column) {
  const left = Number.isFinite(column.rawLeft) ? column.rawLeft : column.left;
  const right = Number.isFinite(column.rawRight) ? column.rawRight : column.right;
  return Math.max(2, Math.min(18, (right - left) * 0.7));
}

export function buildMinuteNetVolumeSeries(
  flowSnaps,
  rows,
  lineData = [],
  windowPts = null,
  snapByChartTime = null
) {
  const bucketMap = new Map();
  for (const snap of flowSnaps || []) {
    if (snap?.hydrationSeam || snap?.hydrationBoundaryTail) continue;
    const observedMinute = minuteBucketMs(snap?.t);
    if (observedMinute == null) continue;
    const observedSnap = snapByChartTime?.get(toChartTime(observedMinute));
    const scopeSpot = snapByChartTime && observedSnap !== undefined
      ? observedSnap?.spot
      : snap?.spot;
    if (Number.isFinite(windowPts) && !Number.isFinite(scopeSpot)) continue;
    const scopedRows = Number.isFinite(windowPts)
      ? Object.keys(snap.byStrike || {})
        .map(Number)
        .filter((strike) => Number.isFinite(strike) && Math.abs(strike - scopeSpot) <= windowPts)
      : rows;
    const totals = netVolByLeg(snap, scopedRows);
    const next = bucketMap.get(observedMinute) || { adjusted: 0, carried: false };
    next.adjusted += totals.adjusted;
    next.carried = next.carried || snap.seriesCarry === true;
    bucketMap.set(observedMinute, next);
  }
  const pointFor = (time, totals) => {
    const { adjusted: value } = totals;
    const result = {
      time,
      value,
      color: value >= 0
        ? BOOKMAP_NET_FLOW_COLORS.positive(0.88)
        : BOOKMAP_NET_FLOW_COLORS.negative(0.9),
    };
    if (totals.carried) result.carried = true;
    return result;
  };
  if (!lineData.length) {
    return Array.from(bucketMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([time, totals]) => pointFor(toChartTime(time), totals));
  }
  return lineData.map((point) => {
    const totals = bucketMap.get(point.time * 1000);
    return totals ? pointFor(point.time, totals) : { time: point.time };
  });
}

export function flowSnapshotsWithLastKnownValues(snaps = [], gaps = []) {
  const flowGaps = (gaps || [])
    .map((gap) => ({
      ...gap,
      from: Number(gap?.from) - 60_000,
      to: Number(gap?.to) - 60_000,
    }))
    .filter((gap) => Number.isFinite(gap.from) && Number.isFinite(gap.to));
  return forwardFillSeriesAtGaps(minuteFlows(snaps), flowGaps, {
    replaceFirstAfterGap: true,
    withTime: (flow, time) => ({ ...flow, t: time, seriesCarry: true }),
  });
}

export function flowSnapshotsForCompletedMinutes(
  snaps = [],
  gaps = [],
  { completionBoundaryMs = null } = {},
) {
  const flows = flowSnapshotsWithLastKnownValues(snaps, gaps);
  if (!Number.isFinite(completionBoundaryMs)) return flows;
  return flows.filter(
    (flow) => Number.isFinite(flow?.t) && flow.t < completionBoundaryMs,
  );
}

export function buildStreamingCandleData(indexBars = [], liveBar = null) {
  const validHistory = (indexBars || [])
    .map((bar) => ({
      timeMs: Number(bar?.time),
      open: Number(bar?.open),
      high: Number(bar?.high),
      low: Number(bar?.low),
      close: Number(bar?.close),
    }))
    .filter((bar) => (
      Number.isFinite(bar.timeMs)
      && Number.isFinite(bar.open)
      && Number.isFinite(bar.high)
      && Number.isFinite(bar.low)
      && Number.isFinite(bar.close)
    ));

  const liveTimeMs = Number(liveBar?.startTime);
  const liveCandidate = {
    timeMs: liveTimeMs,
    open: Number(liveBar?.open),
    high: Number(liveBar?.high),
    low: Number(liveBar?.low),
    close: Number(liveBar?.close),
  };
  const validLive = (
    Number.isFinite(liveCandidate.timeMs)
    && Number.isFinite(liveCandidate.open)
    && Number.isFinite(liveCandidate.high)
    && Number.isFinite(liveCandidate.low)
    && Number.isFinite(liveCandidate.close)
  )
    ? liveCandidate
    : null;

  const anchorMs = validLive?.timeMs
    ?? validHistory[validHistory.length - 1]?.timeMs
    ?? null;
  if (!Number.isFinite(anchorMs)) return [];

  const sessionOpen = sessionOpenEpochSeconds(anchorMs);
  const sessionClose = sessionCloseEpochSeconds(anchorMs);
  const byMinute = new Map();

  const addBar = (bar) => {
    if (!bar) return;
    const time = Math.floor(bar.timeMs / 60_000) * 60;
    if (time < sessionOpen || time > sessionClose) return;
    byMinute.set(time, {
      time,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
    });
  };

  for (const bar of validHistory) addBar(bar);
  // The aggregation-state bar is authoritative for the currently-forming minute.
  // Setting it last replaces a same-minute completed/cache entry rather than
  // creating a duplicate candle.
  addBar(validLive);

  return Array.from(byMinute.values()).sort((a, b) => a.time - b.time);
}

export function candleCloseLineData(candles = []) {
  return (candles || []).map((bar) => (
    Number.isFinite(Number(bar?.close))
      ? {
        time: bar.time,
        value: bar.close,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      }
      : { time: bar.time }
  ));
}

export function candleDataWithLastKnownValues(candles = [], gaps = []) {
  if (!candles.length || !gaps.length) return candles;
  const byTime = new Map(candles.map((bar) => [bar.time, bar]));
  for (const gap of gaps) {
    const prior = candles.findLast((bar) => bar.time * 1000 < gap.from);
    if (!Number.isFinite(Number(prior?.close))) continue;
    const start = Math.ceil(gap.from / 60_000) * 60_000;
    for (let timeMs = start; timeMs < gap.to; timeMs += 60_000) {
      const time = timeMs / 1000;
      if (!rangeContainsTime(gap, timeMs)) continue;
      byTime.set(time, {
        time,
        open: prior.close,
        high: prior.close,
        low: prior.close,
        close: prior.close,
      });
    }
  }
  return [...byTime.values()].sort((left, right) => left.time - right.time);
}

function buildMinuteColumns(snaps, chart, width, height) {
  const timeColumns = buildTimeColumns(snaps, chart, width, height);
  if (!timeColumns.length) return [];
  const minuteColumns = [];
  for (const column of timeColumns) {
    const bucket = minuteBucketMs(column.t);
    const last = minuteColumns[minuteColumns.length - 1];
    if (last && last.t === bucket) {
      last.left = Math.min(last.left, column.left);
      last.right = Math.max(last.right, column.right);
      last.rawLeft = Math.min(last.rawLeft, column.rawLeft);
      last.rawRight = Math.max(last.rawRight, column.rawRight);
      // A minute is on screen if any of its snapshots is.
      last.offPlot = last.offPlot && column.offPlot;
      continue;
    }
    minuteColumns.push({
      t: bucket,
      left: column.left,
      right: column.right,
      rawLeft: column.rawLeft,
      rawRight: column.rawRight,
      x: column.x,
      offPlot: column.offPlot,
    });
  }
  return minuteColumns;
}

export function buildDisplayedDots(flowSnaps, chart, priceSeries, width, height, minVolume) {
  if (!flowSnaps.length || !chart || !priceSeries) return [];
  const plot = plotGeometry(chart, width, height);
  const rawDots = [];
  for (const snap of flowSnaps) {
    const chartX = chart.timeScale().timeToCoordinate(toChartTime(snap.t));
    const x = Number.isFinite(chartX) ? plot.left + chartX : chartX;
    if (!Number.isFinite(x)) continue;
    if (x < plot.left || x > plot.right) continue;
    const {
      dVol,
      dBuy: buy,
      dSell: sell,
      dDeltaBuy,
      dDeltaSell,
      dDeltaNet,
      dDeltaBuyBull,
      dDeltaBuyBear,
      dDeltaSellBull,
      dDeltaSellBear,
      avgStrike,
    } =
      aggregateFlowCells(snap.byStrike, snap.spot);
    if (dVol <= 0) continue;
    const strike = avgStrike;
    const rawY = priceSeries.priceToCoordinate(strike);
    if (!Number.isFinite(rawY)) continue;
    rawDots.push({
      t: snap.t,
      strike,
      cx: x,
      cy: rawY,
      dVol,
      buy,
      sell,
      dDeltaBuy,
      dDeltaSell,
      dDeltaNet,
      dDeltaBuyBull,
      dDeltaBuyBear,
      dDeltaSellBull,
      dDeltaSellBear,
      recoveryGap: Boolean(snap.recoveryGap),
      gapMinutes: Number(snap.gapMinutes) || 0,
    });
  }
  if (!rawDots.length) return [];

  return selectVolumeDots(rawDots, minVolume).map((dot) => ({
    ...dot,
    cy: clampBubbleCenter(dot.cy, dot.r, plot.top, plot.bottom),
  }));
}

function drawLeftStrikeLabels(ctx, rows, chart, priceSeries, width, height, strikeStep) {
  if (!rows.length || !chart || !priceSeries) return;
  const plot = plotGeometry(chart, width, height);
  const x = Math.max(26, plot.left - 2);
  const labels = selectStrikeLabels({
    strikes: rows,
    step: strikeStep,
    toCoordinate: (strike) => priceSeries.priceToCoordinate(strike),
    plotTop: plot.top,
    plotBottom: plot.bottom,
  });
  if (!labels.length) return;

  ctx.save();
  ctx.fillStyle = 'rgba(245,248,252,0.76)';
  ctx.font = "11px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (const label of labels) {
    ctx.fillText(String(label.strike), x, label.y);
  }
  ctx.restore();
}

/**
 * Minute columns projected straight from the chart's visible range.
 *
 * Labels have to appear across the reserved part of the session domain, where
 * no snapshot exists yet, so they cannot be derived from the data. Projecting
 * each minute through the time scale also removes the old dependency on
 * snapshot coverage, which used to drop a cadence boundary that fell inside a
 * gap.
 */
export function buildProjectedColumns(chart, plot, visibleRange, timeScaleData) {
  const step = 60;
  const from = Math.ceil(visibleRange.from / step) * step;
  const to = Math.min(visibleRange.to, sessionCloseEpochSeconds(visibleRange.to * 1000));
  if (!(to > from)) return [];

  const columns = [];
  // Bounded so a pathological range cannot spin: a regular session is 390.
  const maxColumns = 1500;
  for (let t = from, guard = 0; t <= to && guard < maxColumns; t += step, guard += 1) {
    // Through logical space, not `timeToCoordinate`: the reserved region past
    // the last snapshot has no index of its own, so the timestamp route returns
    // null there and the axis stopped at the data.
    const coordinate = chart.timeScale()
      .logicalToCoordinate(timeToLogical(timeScaleData, DEFAULT_BAR_INTERVAL_SECONDS, t));
    if (!Number.isFinite(coordinate)) continue;
    const x = plot.left + coordinate;
    if (x < plot.left || x > plot.right) continue;
    columns.push({ t: t * 1000, x });
  }
  return columns;
}

function buildTimeAxisColumns(snaps, chart, width, height, plot, timeScaleData) {
  // Read the range in logical space and project it back to timestamps: the
  // reserved part of the session domain and the gutter both live past the last
  // column, and `getVisibleRange` clamps to the data, so it reported an axis
  // that ended at the newest snapshot no matter what had been applied.
  let visibleRange = null;
  if (timeScaleData.length) {
    const logical = chart.timeScale().getVisibleLogicalRange();
    if (logical) {
      visibleRange = {
        from: logicalToTime(timeScaleData, DEFAULT_BAR_INTERVAL_SECONDS, Math.max(0, logical.from)),
        to: logicalToTime(timeScaleData, DEFAULT_BAR_INTERVAL_SECONDS, logical.to),
      };
    }
  }
  return visibleRange
    ? buildProjectedColumns(chart, plot, visibleRange, timeScaleData)
    : buildMinuteColumns(snaps, chart, width, height).filter((column) => !column.offPlot);
}

function drawBottomTimeLabels(
  ctx,
  snaps,
  chart,
  width,
  height,
  highlightedTime = null,
  timeScaleData = []
) {
  if (!snaps.length || !chart) return;
  const plot = plotGeometry(chart, width, height);
  const axisTop = plot.bottom;
  const axisHeight = Math.max(0, height - axisTop);
  if (axisHeight <= 0) return;

  const columns = buildTimeAxisColumns(snaps, chart, width, height, plot, timeScaleData);
  if (!columns.length) return;

  ctx.save();
  ctx.fillStyle = 'rgba(5,8,12,0.96)';
  ctx.fillRect(0, axisTop, width, axisHeight);
  ctx.strokeStyle = 'rgba(245,248,252,0.08)';
  ctx.beginPath();
  ctx.moveTo(plot.left, axisTop + 0.5);
  ctx.lineTo(plot.right, axisTop + 0.5);
  ctx.stroke();

  let labels = selectTimeAxisLabels({
    columns,
    plotWidthPx: plot.right - plot.left,
  });
  labels = includeHighlightedTimeAxisLabel(labels, columns, highlightedTime);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = "11px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
  const labelY = axisTop + axisHeight / 2 + 1;
  for (const entry of labels) {
    const isHighlighted = highlightedTime != null && entry.t === highlightedTime;
    ctx.fillStyle = isHighlighted ? PRICE_LINE_COLOR : 'rgba(245,248,252,0.62)';
    ctx.fillText(etTime(entry.t), clamp(entry.x, plot.left + 22, plot.right - 22), labelY);
  }
  ctx.restore();
}

function buildChartOptions(width, height, priceScaleVisible = true) {
  const gridColors = lcGridColors(priceScaleVisible);
  return {
    width,
    height,
    autoSize: false,
    layout: {
      background: { type: ColorType.Solid, color: 'rgba(0,0,0,0)' },
      textColor: 'rgba(245, 248, 252, 0.78)',
      fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
      attributionLogo: false,
    },
    localization: {
      timeFormatter: etChartTime,
      priceFormatter: formatWholePrice,
    },
    rightPriceScale: {
      visible: false,
      borderVisible: false,
    },
    leftPriceScale: {
      visible: priceScaleVisible,
      borderVisible: false,
      textColor: 'rgba(245, 248, 252, 0)',
      minimumWidth: priceScaleVisible ? 26 : 0,
      scaleMargins: {
        top: 0,
        bottom: 0,
      },
    },
    grid: {
      vertLines: { color: gridColors.vertical },
      horzLines: { color: gridColors.horizontal },
    },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: {
        color: BOOKMAP_LC_VISUALS.crosshair.vertical,
        width: 1,
        style: 1,
        labelBackgroundColor: '#05080c',
      },
      horzLine: {
        color: BOOKMAP_LC_VISUALS.crosshair.horizontal,
        width: 1,
        style: 1,
        labelBackgroundColor: '#05080c',
      },
    },
    timeScale: {
      visible: false,
      borderVisible: false,
      timeVisible: true,
      secondsVisible: false,
      // Leave this false. The session domain is 09:30-16:00, and `fixLeftEdge`
      // sizes its scroll floor from that full grid (`width / barSpacing`),
      // which blocks Latest panning after about a bar and shows the prohibited
      // cursor. Premarket is still unrepresentable: `clampVisibleLogicalRange`
      // snaps a negative `from` before display or publish.
      fixLeftEdge: false,
      fixRightEdge: false,

      shiftVisibleRangeOnNewBar: true,
      rightBarStaysOnScroll: true,

      // Pixel mode keeps the close gutter stable while bar spacing changes.
      // Session remains a preset: normal scroll can still move the range.
      barSpacing: BOOKMAP_DEFAULT_BAR_SPACING,
      rightOffsetPixels: RIGHT_GUTTER_BARS * BOOKMAP_DEFAULT_BAR_SPACING,

      // Zero uses the library's width / 2 ceiling, which permits a two-bar view.
      maxBarSpacing: 0,

      tickMarkFormatter: etChartTime,
    },
    handleScroll: {
      mouseWheel: false,
      pressedMouseMove: true,
      horzTouchDrag: true,
      vertTouchDrag: false,
    },
    handleScale: {
      mouseWheel: true,
      pinch: true,
      axisPressedMouseMove: { time: false, price: true },
      axisDoubleClickReset: true,
    },
  };
}

function buildHover(point, snap, chartTime = null) {
  const time = snap?.t ?? (Number.isFinite(chartTime) ? chartTime * 1000 : null);
  if (!point || !Number.isFinite(time)) return null;
  return {
    kind: 'minute-volume',
    t: time,
    x: point.x,
    y: point.y,
    tipX: point.x,
    tipY: point.y,
  };
}

function mergeStrikeCells(left = {}, right = {}) {
  const out = { ...left };
  for (const [key, value] of Object.entries(right)) {
    if (key === 'hasLegSplit') continue;
    if (Number.isFinite(Number(value))) {
      out[key] = (Number(out[key]) || 0) + Number(value);
    } else if (out[key] == null) {
      out[key] = value;
    }
  }
  const leftHasValues = Object.keys(left).length > 0;
  const rightHasValues = Object.keys(right).length > 0;
  if (leftHasValues && rightHasValues) {
    out.hasLegSplit = left.hasLegSplit === true && right.hasLegSplit === true;
  } else if (rightHasValues) {
    out.hasLegSplit = right.hasLegSplit === true;
  } else if (leftHasValues) {
    out.hasLegSplit = left.hasLegSplit === true;
  }
  return out;
}

export function esAdjustedSnap(snap, spxSpot, esSpot) {
  const spot = esAdjustedPrice(snap?.spot, spxSpot, esSpot);
  const hydrationFrom = esAdjustedPrice(snap?.hydrationFrom, spxSpot, esSpot);
  const hydrationTo = esAdjustedPrice(snap?.hydrationTo, spxSpot, esSpot);
  const byStrike = {};
  for (const [strikeKey, cell] of Object.entries(snap?.byStrike || {})) {
    const strike = esAdjustedStrike(Number(strikeKey), spxSpot, esSpot);
    if (strike == null) continue;
    byStrike[strike] = mergeStrikeCells(byStrike[strike], cell);
  }
  return {
    ...snap,
    spot,
    hydrationFrom: hydrationFrom ?? undefined,
    hydrationTo: hydrationTo ?? undefined,
    byStrike,
  };
}

export function BookmapLightweightBase({
  palette = 'bookmap',
  dots = false,
  view = 'spx',
  primitives = false,
}) {
  const isEsSynthetic = view === 'esSynthetic';
  const rawSnaps = useOptionsStore((state) => state.chainSnaps);
  const rawFlowCalculationSnaps = useOptionsStore(
    (state) => state.flowCalculationSnaps || state.chainSnaps,
  );
  const chainSnapshotHydration = useOptionsStore((state) => state.chainSnapshotHydration) || {
    lifecycle: 'ready',
    hasRenderableSeries: true,
  };
  const spxIndexBars = useWebSocketDataStore((state) => state.indexData?.SPX ?? EMPTY_BARS);
  const spxLiveBar = useWebSocketDataStore((state) => state.aggregationState?.SPX ?? null);
  const liveIndexGaps = useWebSocketDataStore((state) => state.liveGapRanges ?? EMPTY_BARS);
  const liveChainGaps = useOptionsStore((state) => state.liveGapRanges ?? EMPTY_BARS);
  const replaying = usePlaybackStore((state) => state.mode === 'playing');
  const replayCoverage = usePlaybackStore((state) => state.replayCoverage);
  const replayCutoffMs = usePlaybackStore((state) => state.replayCutoffMs);
  const liveSpot = useOptionsStore((state) => state.spot);
  const capture = useOptionsStore((state) => state.captureChainSnapshot);
  const exposures = useGreeksWebsocketDataStore(
    (state) => state.greekExposures?.spx,
    (prev, next) => (prev?.length ?? 0) === (next?.length ?? 0)
  );

  const storedLayer = useBookmapSettingsStore((state) => state.heatLayer);
  const setStoredLayer = useBookmapSettingsStore((state) => state.setHeatLayer);
  const [layer, setLayer] = useState(storedLayer);
  const [customVerticalSpan, setCustomVerticalSpan] = useState(null);
  const [timeViewDetached, setTimeViewDetached] = useState(false);
  const timeViewDetachedRef = useRef(false);
  const updateTimeViewDetached = useCallback((detached) => {
    timeViewDetachedRef.current = detached;
    setTimeViewDetached(detached);
  }, []);
  const autoFit = customVerticalSpan == null;
  const [crosshairHover, setCrosshairHover] = useState(null);
  const [synchronizedCrosshairX, setSynchronizedCrosshairX] = useState(null);
  const [displayedDots, setDisplayedDots] = useState([]);
  const [primitiveBubbleSelection, setPrimitiveBubbleSelection] = useState(null);
  const [tipSize, setTipSize] = useState({ w: 200, h: 120 });
  const [viewportVersion, setViewportVersion] = useState(0);
  const [rows, setRows] = useState([]);
  const volumeDotMinVolume = useBookmapSettingsStore((state) => state.volumeDotMinVolume);
  const esSyntheticBasis = useBookmapSettingsStore((state) => state.esSyntheticBasis);
  const setEsSyntheticBasis = useBookmapSettingsStore((state) => state.setEsSyntheticBasis);
  const [basisDraft, setBasisDraft] = useState(String(esSyntheticBasis ?? 0));
  const sharedWindow = useChartTimeWindowStore((state) => state.preference);
  const sharedWindowSource = useChartTimeWindowStore((state) => state.source);
  const publishUserRange = useChartTimeWindowStore((state) => state.publishUserRange);
  const sharedPriceWindow = useChartPriceWindowStore((state) => state.preference);
  const sharedPriceWindowSource = useChartPriceWindowStore((state) => state.source);
  const publishPriceUserRange = useChartPriceWindowStore((state) => state.publishUserRange);
  const resetPriceWindow = useChartPriceWindowStore((state) => state.reset);
  const focusedMinuteMs = useWidgetMinuteFocusStore((state) => state.minuteMs);
  const focusSourceId = useWidgetMinuteFocusStore((state) => state.sourceId);
  // Both Bookmap variants publish under their own id so a target can tell its
  // own intent apart from a sibling's.
  const windowSourceId = primitives ? 'bookmap-primitives' : 'bookmap';

  const mainWrapRef = useRef(null);
  const tipStackRef = useRef(null);
  const mainChartRef = useRef(null);
  const mainOverlayRef = useRef(null);
  const gexOverlayRef = useRef(null);
  const axisOverlayRef = useRef(null);
  const histWrapRef = useRef(null);
  const histCanvasRef = useRef(null);
  const volumeWrapRef = useRef(null);
  const volumeChartRef = useRef(null);
  const volumeCanvasRef = useRef(null);
  const mainApiRef = useRef(null);
  const primitiveRef = useRef(null);
  const volumeApiRef = useRef(null);
  const viewportRafRef = useRef(null);
  const verticalEaseRafRef = useRef(null);
  const expandedVerticalBoundsRef = useRef(null);
  const boundaryAppliedRef = useRef(null);
  const verticalModeKeyRef = useRef(null);
  const prevMainSizeRef = useRef(null);
  const fittedKeyRef = useRef('');
  const autoFitRef = useRef(autoFit);
  const rowsRef = useRef([]);
  const lineDataRef = useRef([]);
  const layerRef = useRef(layer);
  const replaySessionRef = useRef(false);
  const timeScaleDataRef = useRef([]);
  const snapsRef = useRef([]);
  const spotRef = useRef(null);
  const mainSizeRef = useRef({ w: 820, h: 520 });
  const snapMapRef = useRef(new Map());
  const dragStartRef = useRef(null);
  const touchZoomActiveRef = useRef(false);
  const activeTouchPointerIdsRef = useRef(new Set());
  const programmaticRangeUntilRef = useRef(0);
  const programmaticLogicalRangeRef = useRef(null);
  const userViewportIntentUntilRef = useRef(0);
  const userTimeIntentUntilRef = useRef(0);
  const userTimeDragUntilRef = useRef(0);
  const userTimeZoomUntilRef = useRef(0);
  const acceptedLogicalRangeRef = useRef(null);
  const pendingLogicalRangeRef = useRef(null);
  const pendingLogicalZoomRef = useRef(false);
  const timeGestureSettleTimerRef = useRef(null);
  const sharedWindowRef = useRef(sharedWindow);
  const sharedPriceWindowRef = useRef(sharedPriceWindow);
  sharedPriceWindowRef.current = sharedPriceWindow;
  const publishedSharedWindowRef = useRef(null);
  // True while the time range follows stored or default intent. A time gesture
  // clears it until the new span publishes; price-axis gestures do not own it.
  const viewFromPreferenceRef = useRef(false);

  const mainSize = useElementSize(mainWrapRef, { w: 820, h: 520 });
  const histSize = useElementSize(histWrapRef, { w: 260, h: 520 });
  const volumeSize = useElementSize(volumeWrapRef, { w: 820, h: 150 });

  // One step for the whole tile: the price axis and the Net Gamma histogram sit
  // side by side, so a per-surface step would let them disagree at a glance.
  // Derived from the main chart's height because that is the axis the histogram
  // is read against.
  // Measured against the plot band, not the raw panel: `selectStrikeLabels`
  // filters within `plotGeometry` bounds, so feeding the full height here made
  // the step one notch too fine and left the collision filter to thin it out
  // unevenly instead of coarsening cleanly.
  const priceLabelValues = useMemo(() => {
    // The price-scale API is mutable. The viewport version makes this read
    // occur again after a manual scale or pan changes that API state.
    void viewportVersion;
    const mainApi = mainApiRef.current;
    const visible = currentVisiblePriceRange(
      mainApi?.chart,
      mainApi?.priceSeries,
      Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT)
    );
    const values = priceAxisLabelValues(visible);
    return values.length ? values : rows;
  }, [mainSize.h, rows, viewportVersion]);
  const strikeLabelStep = useMemo(() => {
    return chooseStrikeLabelStep({
      strikes: priceLabelValues,
      plotHeightPx: Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT),
    });
  }, [mainSize.h, priceLabelValues]);

  useEffect(() => {
    setBasisDraft(String(esSyntheticBasis ?? 0));
  }, [esSyntheticBasis]);

  useEffect(() => {
    setLayer(storedLayer);
  }, [storedLayer]);

  const observedSnaps = useMemo(() => {
    // Bookmap presents the chain-snapshot session, which ends at the 16:00
    // cash close even though options continue to trade until 16:15.
    const daySnaps = filterBookmapSessionSnapshots(rawSnaps);
    const clipped = daySnaps.length > MAX_RENDER_SNAPS ? daySnaps.slice(-MAX_RENDER_SNAPS) : daySnaps;
    if (!isEsSynthetic) return clipped;

    const spxSpot = sessionAnchoredSpot(liveSpot, clipped);
    if (spxSpot == null) return [];
    const syntheticSpot = spxSpot + Number(esSyntheticBasis ?? 0);
    return clipped.map((snap) => esAdjustedSnap(snap, spxSpot, syntheticSpot));
  }, [esSyntheticBasis, isEsSynthetic, liveSpot, rawSnaps]);
  const observedFlowCalculationSnaps = useMemo(() => {
    const daySnaps = filterBookmapSessionSnapshots(rawFlowCalculationSnaps);
    const clipped = daySnaps.length > MAX_RENDER_SNAPS ? daySnaps.slice(-MAX_RENDER_SNAPS) : daySnaps;
    if (!isEsSynthetic) return clipped;

    const spxSpot = sessionAnchoredSpot(liveSpot, clipped);
    if (spxSpot == null) return [];
    const syntheticSpot = spxSpot + Number(esSyntheticBasis ?? 0);
    return clipped.map((snap) => esAdjustedSnap(snap, spxSpot, syntheticSpot));
  }, [esSyntheticBasis, isEsSynthetic, liveSpot, rawFlowCalculationSnaps]);
  const chainGaps = useMemo(() => {
    const anchorMs = observedSnaps[observedSnaps.length - 1]?.t;
    return mergeSeriesGapRanges(
      replaying
        ? replaySeriesGapRanges(replayCoverage, ['chain'], replayCutoffMs)
        : seriesGapRangesForDate(liveChainGaps, anchorMs),
      denseSeriesGapRanges(observedSnaps, { source: 'chain' }),
    );
  }, [liveChainGaps, observedSnaps, replayCoverage, replayCutoffMs, replaying]);
  const snaps = useMemo(
    () => forwardFillSeriesAtGaps(observedSnaps, chainGaps),
    [chainGaps, observedSnaps],
  );

  const indexBars = useMemo(() => {
    if (!isEsSynthetic) return spxIndexBars;
    const basis = Number(esSyntheticBasis ?? 0);
    return spxIndexBars.map((bar) => ({
      ...bar,
      open: Number.isFinite(Number(bar?.open)) ? Number(bar.open) + basis : bar?.open,
      high: Number.isFinite(Number(bar?.high)) ? Number(bar.high) + basis : bar?.high,
      low: Number.isFinite(Number(bar?.low)) ? Number(bar.low) + basis : bar?.low,
      close: Number.isFinite(Number(bar?.close)) ? Number(bar.close) + basis : bar?.close,
    }));
  }, [esSyntheticBasis, isEsSynthetic, spxIndexBars]);
  const liveIndexBar = useMemo(() => {
    if (!spxLiveBar) return null;
    if (!isEsSynthetic) return spxLiveBar;
    const basis = Number(esSyntheticBasis ?? 0);
    return {
      ...spxLiveBar,
      open: Number.isFinite(Number(spxLiveBar?.open)) ? Number(spxLiveBar.open) + basis : spxLiveBar?.open,
      high: Number.isFinite(Number(spxLiveBar?.high)) ? Number(spxLiveBar.high) + basis : spxLiveBar?.high,
      low: Number.isFinite(Number(spxLiveBar?.low)) ? Number(spxLiveBar.low) + basis : spxLiveBar?.low,
      close: Number.isFinite(Number(spxLiveBar?.close)) ? Number(spxLiveBar.close) + basis : spxLiveBar?.close,
    };
  }, [esSyntheticBasis, isEsSynthetic, spxLiveBar]);
  const newestChainMinuteMs = minuteBucketMs(
    observedFlowCalculationSnaps[observedFlowCalculationSnaps.length - 1]?.t,
  );
  const observedCandleData = useMemo(
    () => buildStreamingCandleData(indexBars, liveIndexBar),
    [indexBars, liveIndexBar]
  );
  const priceGaps = useMemo(() => {
    const anchorMs = observedCandleData[observedCandleData.length - 1]?.time * 1000;
    return mergeSeriesGapRanges(
      replaying
        ? replaySeriesGapRanges(replayCoverage, ['index:SPX'], replayCutoffMs)
        : seriesGapRangesForDate(liveIndexGaps, anchorMs),
      denseSeriesGapRanges(observedCandleData, {
        getTime: (bar) => bar.time * 1000,
        source: 'index:SPX',
      }),
    );
  }, [liveIndexGaps, observedCandleData, replayCoverage, replayCutoffMs, replaying]);
  const candleData = useMemo(
    () => candleDataWithLastKnownValues(observedCandleData, priceGaps),
    [observedCandleData, priceGaps],
  );
  // Keep a close-valued helper on the same streaming candle timestamps for the
  // existing viewport, 0-GEX, tooltip, and primitive calculations. The visible
  // price series itself is candlesticks.
  const lineData = useMemo(() => candleCloseLineData(candleData), [candleData]);
  const syntheticLiveSpot = liveSpot == null ? liveSpot : liveSpot + Number(esSyntheticBasis ?? 0);
  const chainSpot = sessionAnchoredSpot(isEsSynthetic ? syntheticLiveSpot : liveSpot, snaps);
  const streamedSpot = candleData[candleData.length - 1]?.close;
  const spot = Number.isFinite(streamedSpot) ? streamedSpot : chainSpot;
  const timeScaleAnchor = lineData[0]?.time ?? null;
  const timeScaleData = useMemo(
    () => buildSessionTimeScaleData(timeScaleAnchor),
    [timeScaleAnchor]
  );
  const baseRows = useMemo(() => hydratedStrikeRows(snaps, lineData, spot), [lineData, snaps, spot]);
  const flowSnaps = useMemo(
    () => flowSnapshotsForCompletedMinutes(observedFlowCalculationSnaps, chainGaps, {
      completionBoundaryMs: newestChainMinuteMs,
    }),
    [chainGaps, newestChainMinuteMs, observedFlowCalculationSnaps],
  );
  const zeroGexCrossings = useMemo(() => {
    const series = gammaNetExposureSeries(exposures)
      .filter((item) => isDuringMarketHours(item.t));
    return zeroGexCrossingsFromSeries(series, snaps);
  }, [exposures, snaps]);
  const latestZeroGex = zeroGexCrossings.length > 0
    ? zeroGexCrossings[zeroGexCrossings.length - 1]
    : null;
  const zeroGexTimes = useMemo(
    () => zeroGexCrossingTimes(zeroGexCrossings, snaps),
    [zeroGexCrossings, snaps]
  );
  const snapMap = useMemo(() => {
    const next = new Map();
    for (const snap of snaps) next.set(toChartTime(snap.t), snap);
    return next;
  }, [snaps]);
  const observedFlowSnaps = useMemo(
    () => flowSnaps.filter((snap) => (
      !snap?.hydrationSeam
      && !snap?.hydrationBoundaryTail
      && Number.isFinite(snap?.t)
      && Number.isFinite(snap?.spot)
    )),
    [flowSnaps]
  );
  const observedGrossVolumeMap = useMemo(() => {
    const next = new Map();
    for (const snap of observedFlowSnaps) {
      next.set(toChartTime(snap.t), {
        value: aggregateFlowCells(snap.byStrike, snap.spot, STRIKE_WINDOW_PTS).dVol,
        recoveryGap: snap.recoveryGap === true,
        gapMinutes: Number(snap.gapMinutes) || 0,
      });
    }
    return next;
  }, [observedFlowSnaps]);

  const viewLabel = snaps.length
    ? `${etTime(snaps[0].t)} - ${etTime(snaps[snaps.length - 1].t)}`
    : '--';
  const spotLabel = isEsSynthetic ? 'ES synth' : 'SPX';
  const sessionDateLabel = snaps.length ? fmtSessionDateET(snaps[0].t) : '--';
  const renderedSessionDate = snaps.length ? etDateKey(snaps[snaps.length - 1].t) : null;
  const isReplaySession = replaying && chainSnapshotHydration.source === 'replay';
  const isLatestLiveSession = renderedSessionDate === mostRecentSessionDate();
  const ready = baseRows.length > 0
    && snaps.length > 0
    && ['fallback', 'ready', 'degraded'].includes(chainSnapshotHydration.lifecycle)
    && (isReplaySession || isLatestLiveSession);
  const hydrationMessage = flowmapHydrationMessage(chainSnapshotHydration, snaps);

  // ORIGINAL NET VOLUME SOURCE
  const perMinNetVol = useMemo(
    () => buildMinuteNetVolumeSeries(flowSnaps, [], lineData, STRIKE_WINDOW_PTS, snapMap),
    [flowSnaps, lineData, snapMap]
  );

  // TEMP TEST: 1-minute Net Delta
  // Keep the existing perMinNetVol variable name so the rest of the
  // histogram/rendering code does not need to change.
  // const perMinNetVol = useMemo(() => {
  //   const bucketMap = new Map();
  //
  //   for (const snap of flowSnaps || []) {
  //     const bucket = minuteBucketMs(snap?.t);
  //     if (bucket == null) continue;
  //
  //     const { dDeltaNet } = aggregateFlowCells(
  //       snap.byStrike,
  //       snap.spot
  //     );
  //
  //     bucketMap.set(
  //       bucket,
  //       (bucketMap.get(bucket) || 0) + (Number(dDeltaNet) || 0)
  //     );
  //   }
  //
  //   const pointFor = (time, value) => ({
  //     time,
  //     value,
  //     color: value >= 0
  //       ? BOOKMAP_NET_FLOW_COLORS.positive(0.88)
  //       : BOOKMAP_NET_FLOW_COLORS.negative(0.9),
  //   });
  //
  //   if (!lineData.length) {
  //     return Array.from(bucketMap.entries())
  //       .sort((a, b) => a[0] - b[0])
  //       .map(([time, value]) =>
  //         pointFor(toChartTime(time), value)
  //       );
  //   }
  //
  //   return lineData.map((point) => {
  //     const value = bucketMap.get(point.time * 1000);
  //
  //     return value != null
  //       ? pointFor(point.time, value)
  //       : { time: point.time };
  //   });
  // }, [flowSnaps, lineData]);

  const volumeSummary = useMemo(() => {
    if (!flowSnaps.length || !perMinNetVol.length) {
      return { latest: null, peak: null, total: 0 };
    }
    let peak = null;
    let latest = null;
    let total = 0;
    for (const item of perMinNetVol) {
      if (!Number.isFinite(item.value)) continue;
      if (!item.carried) total += item.value;
      if (item.value) latest = { t: item.time * 1000, value: item.value };
      if (!peak || Math.abs(item.value) > Math.abs(peak.value)) {
        peak = { t: item.time * 1000, value: item.value };
      }
    }
    return { latest, peak, total };
  }, [flowSnaps, perMinNetVol]);

  useEffect(() => acquireBookmapSampler(capture), [capture]);

  useEffect(() => () => {
    if (viewportRafRef.current) window.cancelAnimationFrame(viewportRafRef.current);
    if (verticalEaseRafRef.current) window.cancelAnimationFrame(verticalEaseRafRef.current);
  }, []);

  useEffect(() => {
    if (!rows.length && baseRows.length && !sameStrikeRows(rows, baseRows)) {
      setRows(baseRows);
    }
  }, [baseRows, rows]);

  useEffect(() => {
    autoFitRef.current = autoFit;
  }, [autoFit]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    lineDataRef.current = lineData;
  }, [lineData]);

  useEffect(() => {
    layerRef.current = layer;
  }, [layer]);

  useEffect(() => {
    replaySessionRef.current = isReplaySession;
  }, [isReplaySession]);

  useEffect(() => {
    timeScaleDataRef.current = timeScaleData;
  }, [timeScaleData]);

  useEffect(() => {
    snapsRef.current = snaps;
  }, [snaps]);

  useEffect(() => {
    spotRef.current = spot;
  }, [spot]);

  useEffect(() => {
    mainSizeRef.current = mainSize;
  }, [mainSize]);

  useEffect(() => {
    snapMapRef.current = snapMap;
  }, [snapMap]);

  const markProgrammaticRange = useCallback(() => {
    programmaticRangeUntilRef.current = window.performance.now() + 250;
  }, []);

  // Time intent governs publishing; viewport intent additionally governs
  // disarming the price-scale auto-fit. They are separate because a plot drag on
  // the primitives variant pans time while its price scale stays owned by the
  // primitive.
  const markUserTimeIntent = useCallback((ms = 600) => {
    userTimeIntentUntilRef.current = window.performance.now() + ms;
    viewFromPreferenceRef.current = false;
  }, []);

  const markUserViewportIntent = useCallback((ms = 600) => {
    userViewportIntentUntilRef.current = window.performance.now() + ms;
  }, []);

  const markUserTimeCustomIntent = useCallback((ms = 600) => {
    markUserTimeIntent(ms);
    userTimeDragUntilRef.current = window.performance.now() + ms;
  }, [markUserTimeIntent]);

  const markUserTimeZoomIntent = useCallback((ms = 600) => {
    markUserTimeCustomIntent(ms);
    userTimeZoomUntilRef.current = window.performance.now() + ms * 2;
  }, [markUserTimeCustomIntent]);

  const correctInteractiveTimeRange = useCallback(() => {
    const mainApi = mainApiRef.current;
    const candidate = pendingLogicalRangeRef.current;
    const scaleData = timeScaleDataRef.current;
    if (!mainApi || !candidate || !scaleData.length) return;

    const span = Number(candidate.to) - Number(candidate.from);
    const close = sessionCloseLogical(scaleData);
    const ceiling = Number.isFinite(close)
      ? close + gutterForVisibleSpan(scaleData, span)
      : Infinity;
    const corrected = clampVisibleLogicalRange(candidate, {
      previous: { from: 0, to: span },
      maxTo: ceiling,
    });
    if (!corrected || sameNumericRange(candidate, corrected)) return;

    markProgrammaticRange();
    programmaticLogicalRangeRef.current = corrected;
    pendingLogicalRangeRef.current = corrected;
    acceptedLogicalRangeRef.current = corrected;
    mainApi.chart.timeScale().setVisibleLogicalRange(corrected);
  }, [markProgrammaticRange]);

  const scheduleInteractiveTimeCorrection = useCallback((delay = 0) => {
    if (timeGestureSettleTimerRef.current) {
      window.clearTimeout(timeGestureSettleTimerRef.current);
    }
    timeGestureSettleTimerRef.current = window.setTimeout(() => {
      timeGestureSettleTimerRef.current = null;
      correctInteractiveTimeRange();
    }, delay);
  }, [correctInteractiveTimeRange]);

  useEffect(() => () => {
    if (timeGestureSettleTimerRef.current) {
      window.clearTimeout(timeGestureSettleTimerRef.current);
    }
  }, []);

  const disarmAutoFit = useCallback(() => {
    if (!autoFitRef.current) return;
    const range = currentVisiblePriceRange(
      mainApiRef.current?.chart,
      mainApiRef.current?.priceSeries,
      Math.max(1, mainSizeRef.current.h - BOOKMAP_TIME_AXIS_HEIGHT)
    );
    autoFitRef.current = false;
    const currentSpan = Number(range?.to) - Number(range?.from);
    const minimumSpan = (BOOKMAP_STRIKE_RANGE.minStrikes - 1) * BOOKMAP_STRIKE_RANGE.step;
    setCustomVerticalSpan(Number.isFinite(currentSpan) && currentSpan > 0
      ? currentSpan
      : minimumSpan);
    if (primitives) mainApiRef.current?.chart.priceScale('left').setAutoScale(false);
  }, [primitives]);

  const applyAutoFit = useCallback(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi) return { rows: rowsRef.current, scaleChanged: false };
    // The primitives variant used to hand the price scale to lightweight-charts'
    // autoscale, which fits the price line alone and knows nothing about strikes,
    // so a quiet session rendered a far shorter axis than the other tabs.
    const mode = sharedWindowRef.current?.mode === WINDOW_MODE.SESSION
      ? WINDOW_MODE.SESSION
      : WINDOW_MODE.LATEST;
    const sessionDate = snapsRef.current.length
      ? etDateKey(snapsRef.current[snapsRef.current.length - 1].t)
      : 'none';
    const modeKey = verticalFitScopeKey(
      replaySessionRef.current,
      sessionDate,
      mode,
      layerRef.current
    );
    if (verticalModeKeyRef.current !== modeKey) {
      verticalModeKeyRef.current = modeKey;
      expandedVerticalBoundsRef.current = null;
    }
    const result = fitPriceScaleToVisibleData(
      mainApi.chart,
      snapsRef.current,
      lineDataRef.current,
      spotRef.current,
      setRows,
      mainSizeRef.current.w,
      mainSizeRef.current.h,
      viewportRafRef,
      setViewportVersion,
      layerRef.current,
      expandedVerticalBoundsRef.current,
      mode === WINDOW_MODE.SESSION
    );
    if (result.rows?.length) {
      // `applyAutoFit` runs per frame from `handleRange`, and a full `setData`
      // on both boundary series each time is pure waste: their points are the
      // fitted edges repeated across the line data, so they only change when
      // that range or the line data does.
      syncBoundarySeries(
        boundaryAppliedRef,
        mainApi,
        lineDataRef.current,
        result.rows,
        result.bounds,
        { skipUnchanged: true }
      );
    }
    if (result.bounds) expandedVerticalBoundsRef.current = result.bounds;
    if (result.rows?.length) rowsRef.current = result.rows;
    return result;
  }, []);
  // The createChart effect reads this so a volume-threshold change cannot
  // remount the chart and reset the time scale into premarket. Synced in an
  // effect rather than during render: a discarded concurrent render must not
  // leave the live range subscriber fitting to a threshold that never
  // committed.
  const applyAutoFitRef = useRef(applyAutoFit);
  useEffect(() => {
    applyAutoFitRef.current = applyAutoFit;
  }, [applyAutoFit]);

  const syncManualPriceRows = useCallback((range) => {
    const nextRows = visibleStrikeRowsForPriceRange(
      snapsRef.current,
      range,
      spotRef.current,
      BOOKMAP_STRIKE_RANGE
    );
    if (!nextRows.length) return rowsRef.current;

    rowsRef.current = nextRows;
    setRows((prev) => (sameStrikeRows(prev, nextRows) ? prev : nextRows));

    // Keep the invisible boundary series in step with the manual viewport too.
    // This matters because the visible axis is custom and the native price scale
    // is hidden; the boundary series remains the fallback owner of projection.
    const mainApi = mainApiRef.current;
    if (mainApi) {
      syncBoundarySeries(
        boundaryAppliedRef,
        mainApi,
        lineDataRef.current,
        nextRows
      );
    }

    return nextRows;
  }, []);

  const publishPriceRange = useCallback((range) => {
    publishPriceUserRange(windowSourceId, range, { tradingDate: renderedSessionDate });
  }, [publishPriceUserRange, renderedSessionDate, windowSourceId]);

  // Both panes must show the same logical range. The volume chart re-fits its
  // own time scale whenever its data or its size changes -- `histogram.setData`,
  // `gridSeries.setData` and the resize all do -- so mirroring the main range
  // only when the main range itself changed let the two drift apart.
  const syncVolumeTimeScale = useCallback((range = null) => {
    const volumeChart = volumeApiRef.current?.chart;
    const mainChart = mainApiRef.current?.chart;
    if (!volumeChart || !mainChart) return;
    let target = range;
    if (!target) {
      try { target = mainChart.timeScale().getVisibleLogicalRange(); } catch { return; }
    }
    if (!target) return;
    try {
      // The volume chart takes no gestures of its own, so this cannot feed back.
      volumeChart.timeScale().setVisibleLogicalRange(target);
    } catch { /* the volume chart can be rebuilt under a pending frame */ }
  }, []);

  const applyCustomVerticalRange = useCallback(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi || !Number.isFinite(customVerticalSpan) || !Number.isFinite(spotRef.current)) {
      return false;
    }
    // Easing toward spot during a pointer gesture fights the range captured at
    // pointerdown. A body pan keeps its translated range after release.
    if (dragStartRef.current) return false;
    const current = currentVisiblePriceRange(
      mainApi.chart,
      mainApi.priceSeries,
      Math.max(1, mainSizeRef.current.h - BOOKMAP_TIME_AXIS_HEIGHT)
    );
    const target = clampManualPriceBounds(
      recenterSpotRange(
        current,
        spotRef.current,
        customVerticalSpan,
        undefined,
        BOOKMAP_STRIKE_RANGE.manualMinSpan
      ),
      manualPriceCeiling(snapsRef.current, spotRef.current)
    );
    if (!target || sameNumericRange(current, target)) return false;

    if (verticalEaseRafRef.current) {
      window.cancelAnimationFrame(verticalEaseRafRef.current);
    }
    const start = current || target;
    const startedAt = window.performance.now();
    const step = (now) => {
      const frameNow = Number.isFinite(now)
        ? now
        : startedAt + BOOKMAP_VERTICAL_EASE_MS;
      const progress = Math.min(
        1,
        Math.max(0, (frameNow - startedAt) / BOOKMAP_VERTICAL_EASE_MS)
      );
      const eased = 1 - ((1 - progress) ** 3);
      const range = {
        from: start.from + (target.from - start.from) * eased,
        to: start.to + (target.to - start.to) * eased,
      };
      mainApi.chart.priceScale('left').setAutoScale(false);
      mainApi.chart.priceScale('left').setVisibleRange(range);
      syncManualPriceRows(range);
      scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      if (progress < 1) {
        verticalEaseRafRef.current = window.requestAnimationFrame(step);
      } else {
        verticalEaseRafRef.current = null;
      }
    };
    verticalEaseRafRef.current = window.requestAnimationFrame(step);
    return true;
  }, [customVerticalSpan, syncManualPriceRows]);

  const applySharedPriceWindow = useCallback(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi || dragStartRef.current) return false;

    const preference = sharedPriceWindowRef.current;
    if (!preference) {
      if (useChartPriceWindowStore.getState().source == null) return false;
      autoFitRef.current = true;
      setCustomVerticalSpan(null);
      expandedVerticalBoundsRef.current = null;
      lastLockedRangeByChart.delete(mainApi.chart);
      applyAutoFitRef.current();
      scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      return true;
    }

    const target = clampManualPriceBounds(
      preference,
      manualPriceCeiling(snapsRef.current, spotRef.current)
    );
    if (!target) return false;

    autoFitRef.current = false;
    setCustomVerticalSpan(target.to - target.from);
    const current = currentVisiblePriceRange(
      mainApi.chart,
      mainApi.priceSeries,
      Math.max(1, mainSizeRef.current.h - BOOKMAP_TIME_AXIS_HEIGHT)
    );
    if (!sameNumericRange(current, target)) {
      mainApi.chart.priceScale('left').setAutoScale(false);
      mainApi.chart.priceScale('left').setVisibleRange(target);
    }
    syncManualPriceRows(target);
    scheduleViewportRefresh(viewportRafRef, setViewportVersion);
    return true;
  }, [syncManualPriceRows]);

  // Keyed on the lineData array identity: this runs on every published range
  // change, and rebuilding plus re-measuring the series each frame of a pan is
  // pure waste when the series has not changed.
  const windowContextCacheRef = useRef({ source: null, value: null });

  const windowContext = useCallback(() => {
    const lineData = lineDataRef.current;
    const cache = windowContextCacheRef.current;
    if (cache.source === lineData) return cache.value;

    const times = lineData.map((point) => point.time);
    const value = {
      extent: clampExtentToSessionClose(extentFromEpochSeconds(times)),
      barIntervalSeconds: inferBarIntervalSeconds(times),
    };
    windowContextCacheRef.current = { source: lineData, value };
    return value;
  }, []);

  const publishVisibleRange = useCallback((range, options = {}) => {
    const { allowProgrammatic = false, ...publishOptions } = options;
    if (!allowProgrammatic
      && window.performance.now() <= programmaticRangeUntilRef.current) return;
    const from = Number(range?.from);
    const to = Number(range?.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return;
    const published = publishUserRange(
      windowSourceId,
      { from, to },
      extent,
      {
        barIntervalSeconds,
        tradingDate: etDateKey(extent.last * 1000),
        ...publishOptions,
      }
    );
    if (published) {
      publishedSharedWindowRef.current = useChartTimeWindowStore.getState().preference;
      viewFromPreferenceRef.current = true;
      updateTimeViewDetached(false);
    }
  }, [publishUserRange, updateTimeViewDetached, windowContext, windowSourceId]);

  // Lightweight Charts can emit several range changes per frame during a drag.
  // Each publish wakes every subscriber -- including the price chart mounted
  // behind this tab, which then writes into its iframe -- so collapse them to
  // one per frame. The last range in a frame is the one that matters.
  const pendingPublishRef = useRef(null);
  const publishRafRef = useRef(null);

  const cancelPendingPublish = useCallback(() => {
    if (publishRafRef.current) {
      window.cancelAnimationFrame(publishRafRef.current);
      publishRafRef.current = null;
    }
    pendingPublishRef.current = null;
  }, []);

  const schedulePublish = useCallback((range, options = {}) => {
    pendingPublishRef.current = { range, options };
    if (publishRafRef.current) return;
    publishRafRef.current = window.requestAnimationFrame(() => {
      publishRafRef.current = null;
      const pending = pendingPublishRef.current;
      pendingPublishRef.current = null;
      if (pending) publishVisibleRange(pending.range, pending.options);
    });
  }, [publishVisibleRange]);

  useEffect(() => cancelPendingPublish, [cancelPendingPublish]);

  /**
   * Resolve the shared preference against this chart's own data and apply it.
   * Returns false when nothing applicable is stored, which is the caller's
   * signal to fall back to its own default. Never writes to the store: a
   * clamped partial application must leave the original intent intact.
   */
  const applySharedWindow = useCallback(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi) return false;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return false;

    const { status, range } = resolveWindowPreference(sharedWindowRef.current, extent, {
      barIntervalSeconds,
    });
    if (status === RESOLUTION_STATUS.UNAVAILABLE || !range) return false;

    markProgrammaticRange();
    try {
      const applied = applyLogicalTimeRange(
        mainApi.chart,
        timeScaleDataRef.current,
        barIntervalSeconds,
        range,
        lineDataRef.current[lineDataRef.current.length - 1]?.time
      );
      if (!applied) return false;
    } catch {
      // The chart can be recreated before its series has accepted new points.
      return false;
    }
    viewFromPreferenceRef.current = true;
    scheduleSettledViewportRefresh(viewportRafRef, setViewportVersion);
    return true;
  }, [markProgrammaticRange, windowContext]);

  /**
   * Automatic refit when the data span changes. Never user-initiated: the
   * explicit paths are the time-window segments, so this stays silent and
   * must not publish. Price-axis gestures disarm auto-fit until remount.
   */
  const fitCharts = useCallback(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi) return;
    const fitLineData = lineDataRef.current;
    const fitKey = fitLineData.length
      ? `${fitLineData[0].time}-${fitLineData[fitLineData.length - 1].time}`
      : '';
    const shouldRefitTime = Boolean(fitKey) && fitKey !== fittedKeyRef.current;
    if (shouldRefitTime && fitLineData.length) {
      markProgrammaticRange();
      const applied = applyDefaultRollingTimeRange(
        mainApi.chart,
        fitLineData,
        DEFAULT_VISIBLE_SECONDS,
        timeScaleDataRef.current
      );
      if (applied) viewFromPreferenceRef.current = true;
    }
    applyAutoFit();
    fittedKeyRef.current = fitKey;
    // A time refit is a programmatic range change, so it races the library's
    // layout the same way a remount does. This helper is only called when the
    // data span changed, so shouldRefitTime is always true here.
    if (shouldRefitTime) {
      scheduleSettledViewportRefresh(viewportRafRef, setViewportVersion);
    }
  }, [applyAutoFit, markProgrammaticRange]);

  /**
   * Apply a time-window mode chosen from the header control.
   *
   * Time only: the price scale is a separate auto-fit, so this deliberately
   * never touches `autoFit`. Selecting a mode is user intent by definition,
   * so unlike the programmatic paths it publishes.
   */
  const applyTimeWindowMode = useCallback((mode) => {
    const mainApi = mainApiRef.current;
    const fitLineData = lineDataRef.current;
    if (!mainApi || !fitLineData.length) return;
    const { extent, barIntervalSeconds } = windowContext();
    if (!extent) return;

    // An explicit selection supersedes any gesture still settling. Otherwise
    // its 600ms intent window outlives the 250ms programmatic guard and can
    // republish Session/Latest as Custom.
    cancelPendingPublish();
    updateTimeViewDetached(false);
    userTimeIntentUntilRef.current = 0;
    userTimeDragUntilRef.current = 0;
    userTimeZoomUntilRef.current = 0;
    viewFromPreferenceRef.current = false;
    expandedVerticalBoundsRef.current = null;
    verticalModeKeyRef.current = null;

    let applied = null;
    markProgrammaticRange();
    if (mode === WINDOW_MODE.SESSION) {
      const domain = sessionDomainSeconds(msFromSeconds(extent.last));
      const range = { from: domain.first, to: domain.last };
      applied = applyLogicalTimeRange(
        mainApi.chart,
        timeScaleDataRef.current,
        barIntervalSeconds,
        range,
        fitLineData[fitLineData.length - 1].time
      )
        ? range
        : null;
    } else {
      applied = applyDefaultRollingTimeRange(
        mainApi.chart,
        fitLineData,
        DEFAULT_VISIBLE_SECONDS,
        timeScaleDataRef.current
      );
    }
    if (!applied) return;
    // Publish on every path, `Custom` included. Clearing the preference instead
    // would strand the sibling variant and the price chart on the interior
    // range, because both consumers early-return on a null preference. The
    // rolling range classifies as `latest`, so the reset lands everywhere and
    // leaves a benign default rather than a stale interval.
    const publishedRange = mode === WINDOW_MODE.LATEST
      ? { from: extent.last - DEFAULT_VISIBLE_SECONDS, to: extent.last }
      : applied;
    publishVisibleRange(publishedRange, {
      allowProgrammatic: true,
      isDefaultSpan: mode === WINDOW_MODE.LATEST ? true : undefined,
    });
    scheduleSettledViewportRefresh(viewportRafRef, setViewportVersion);
  }, [
    cancelPendingPublish,
    markProgrammaticRange,
    publishVisibleRange,
    updateTimeViewDetached,
    windowContext,
  ]);

  useEffect(() => {
    if (!ready || !mainChartRef.current) return undefined;
    acceptedLogicalRangeRef.current = null;
    pendingLogicalRangeRef.current = null;
    pendingLogicalZoomRef.current = false;
    const initialHeight = mainChartRef.current.clientHeight || 520;
    const chart = createChart(mainChartRef.current, buildChartOptions(
      mainChartRef.current.clientWidth || 820,
      Math.max(1, initialHeight - BOOKMAP_TIME_AXIS_HEIGHT),
      true
    ));
    chart.applyOptions({ leftPriceScale: { visible: false, minimumWidth: 0 } });
    bookmapPlotGutters.set(chart, BOOKMAP_STRIKE_GUTTER_WIDTH);
    hiddenNativePriceScaleCharts.add(chart);
    const priceSeries = chart.addSeries(CandlestickSeries, {
      // Up candles: hollow
      upColor: 'rgba(0,0,0,0)',

      // Down candles: solid white
      downColor: '#ffffff',

      // White wicks
      wickUpColor: '#ffffff',
      wickDownColor: '#ffffff',

      // White borders
      borderUpColor: '#ffffff',
      borderDownColor: '#ffffff',

      borderVisible: true,
      wickVisible: true,

      priceScaleId: 'left',
      lastValueVisible: false,
      priceLineVisible: false,

      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
    });
    const upperSeries = chart.addSeries(LineSeries, {
      color: 'rgba(0,0,0,0)',
      lineWidth: 1,
      priceScaleId: 'left',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });
    const lowerSeries = chart.addSeries(LineSeries, {
      color: 'rgba(0,0,0,0)',
      lineWidth: 1,
      priceScaleId: 'left',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });
    const timeScaleSeries = chart.addSeries(LineSeries, {
      color: 'rgba(0,0,0,0)',
      lineVisible: false,
      priceScaleId: 'time-grid',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });

    let lastLogicalRange = null;
    const handleRange = (range) => {
      if (!range) {
        lastLogicalRange = null;
        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
        return;
      }
      const scaleData = timeScaleDataRef.current;
      const now = window.performance.now();
      const nextSpan = Number(range.to) - Number(range.from);
      const sessionClose = sessionCloseLogical(scaleData);
      const sessionCeiling = Number.isFinite(sessionClose)
        ? sessionClose + gutterForVisibleSpan(scaleData, nextSpan)
        : Infinity;
      const zoomIntentActive = now <= userTimeZoomUntilRef.current;
      const bodyPanActive = now <= userTimeDragUntilRef.current && !zoomIntentActive;
      const userGestureActive = zoomIntentActive || bodyPanActive;
      const programmaticEcho = sameNumericRange(range, programmaticLogicalRangeRef.current);
      if (programmaticEcho) programmaticLogicalRangeRef.current = null;
      const isProgrammaticRange = !userGestureActive
        && (programmaticEcho || now <= programmaticRangeUntilRef.current);
      const previousForClamp = bodyPanActive
        ? { from: 0, to: nextSpan }
        : lastLogicalRange;
      let visible = clampVisibleLogicalRange(range, {
        previous: previousForClamp,
        maxTo: sessionCeiling,
      });
      if (!visible) {
        lastLogicalRange = null;
        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
        return;
      }
      if (!sameNumericRange(visible, range)) {
        markProgrammaticRange();
        programmaticLogicalRangeRef.current = visible;
        chart.timeScale().setVisibleLogicalRange(visible);
      }

      // Scale gestures set a rolling span; body pans publish the position they
      // land on. Both are shared intent -- a pan that stayed local left this
      // chart somewhere the price chart and the GEX widgets never followed.
      if (bodyPanActive && !programmaticEcho && lastLogicalRange) {
        // Only while the span holds. A pan is a translate by definition, so a
        // pan-classified range whose span moved is classification noise, and
        // publishing it as a position would push that span onto every other
        // chart. The price chart guards its own pan the same way.
        const heldSpan = Math.abs(
          (visible.to - visible.from) - (lastLogicalRange.to - lastLogicalRange.from)
        ) <= 1e-6;
        if (heldSpan) {
          const panScaleData = timeScaleDataRef.current;
          schedulePublish({
            from: logicalToTime(panScaleData, DEFAULT_BAR_INTERVAL_SECONDS, visible.from),
            to: logicalToTime(panScaleData, DEFAULT_BAR_INTERVAL_SECONDS, visible.to),
          }, { positionIsIntent: true });
        }
      }

      if (zoomIntentActive && !programmaticEcho) {
        const fitLineData = lineDataRef.current;
        const dataLastTime = fitLineData[fitLineData.length - 1]?.time;
        const dataLastLogical = lastDataLogicalIndex(
          scaleData,
          fitLineData,
          DEFAULT_BAR_INTERVAL_SECONDS
        );
        if (Number.isFinite(dataLastLogical)) {
          const visibleSpan = visible.to - visible.from;
          const estimatedGutter = gutterForVisibleSpan(scaleData, visibleSpan);
          const measuredContentSpan = Math.max(1, visibleSpan - estimatedGutter);
          const availableSpan = Math.max(1, dataLastLogical);
          const contentSpan = Math.max(
            measuredContentSpan,
            Math.min(MIN_VISIBLE_BARS, availableSpan)
          );
          const from = Math.max(0, dataLastLogical - contentSpan);
          const to = dataLastLogical + gutterForContentSpan(
            scaleData,
            dataLastLogical - from
          );
          const anchored = { from, to };
          if (!sameNumericRange(anchored, visible)) {
            markProgrammaticRange();
            programmaticLogicalRangeRef.current = anchored;
            chart.timeScale().setVisibleLogicalRange(anchored);
            visible = anchored;
          }
          schedulePublish({
            from: logicalToTime(scaleData, DEFAULT_BAR_INTERVAL_SECONDS, from),
            to: dataLastTime,
          }, { allowProgrammatic: true });
        }
      }

      pendingLogicalRangeRef.current = visible;
      pendingLogicalZoomRef.current = zoomIntentActive;
      acceptedLogicalRangeRef.current = visible;
      syncVolumeTimeScale(visible);
      const rangeMoved = !sameNumericRange(lastLogicalRange, visible);
      if (rangeMoved) lastLogicalRange = { from: visible.from, to: visible.to };
      if (isProgrammaticRange) {
        const fitResult = autoFitRef.current ? applyAutoFitRef.current() : null;
        if (rangeMoved || fitResult?.scaleChanged) {
          scheduleViewportRefresh(viewportRafRef, setViewportVersion);
        }
        return;
      }
      let fitResult = null;
      if (window.performance.now() <= userViewportIntentUntilRef.current) {
        disarmAutoFit();
      } else if (autoFitRef.current) {
        fitResult = applyAutoFitRef.current();
      }
      if (rangeMoved || fitResult?.scaleChanged) {
        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      }
    };
    const handleCrosshair = (param) => {
      if (!param?.time || !param.point) {
        setCrosshairHover(null);
        setPrimitiveBubbleSelection(null);
        useWidgetMinuteFocusStore.getState().clearFocusedMinute('flowmap');
        return;
      }
      useWidgetMinuteFocusStore.getState().setFocusedMinute(
        'flowmap',
        Number(param.time) * 1000,
      );
      const snap = snapMapRef.current.get(Number(param.time));
      const primitive = primitiveRef.current;
      const reportedBubbleId = param.hoveredInfo?.objectId ?? param.hoveredObjectId;
      const directBubbleId = reportedBubbleId == null
        ? primitive?.hitTest(param.point.x, param.point.y)?.externalId
        : null;
      const bubble = primitive?.bubbleForId(reportedBubbleId ?? directBubbleId)
        ?? primitive?.bubbleAtChartTime(param.time);
      setPrimitiveBubbleSelection(bubble ? { id: bubble.id, time: Number(param.time) } : null);
      setCrosshairHover(buildHover(
        { ...param.point, x: param.point.x + BOOKMAP_STRIKE_GUTTER_WIDTH },
        snap,
        Number(param.time)
      ));
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleRange);
    chart.subscribeCrosshairMove(handleCrosshair);

    if (primitives) {
      const primitive = new BookmapMainPrimitive();
      priceSeries.attachPrimitive(primitive);
      primitiveRef.current = primitive;
    }
    mainApiRef.current = { chart, priceSeries, upperSeries, lowerSeries, timeScaleSeries };
    // The data effect locks the scale after series data exists. Locking here
    // would record the same range against empty series, and the later skip
    // would leave autoscale on price-line extrema.
    scheduleSettledViewportRefresh(viewportRafRef, setViewportVersion);
    return () => {
      if (verticalEaseRafRef.current) {
        window.cancelAnimationFrame(verticalEaseRafRef.current);
        verticalEaseRafRef.current = null;
      }
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRange);
      chart.unsubscribeCrosshairMove(handleCrosshair);
      useWidgetMinuteFocusStore.getState().clearFocusedMinute('flowmap');
      acceptedLogicalRangeRef.current = null;
      pendingLogicalRangeRef.current = null;
      pendingLogicalZoomRef.current = false;
      boundaryAppliedRef.current = null;
      mainApiRef.current = null;
      primitiveRef.current = null;
      chart.remove();
    };
  }, [
    disarmAutoFit,
    correctInteractiveTimeRange,
    markProgrammaticRange,
    primitives,
    ready,
    schedulePublish,
    syncVolumeTimeScale,
    windowContext,
  ]);

  useEffect(() => {
    if (focusSourceId === 'flowmap') {
      setSynchronizedCrosshairX(null);
      return;
    }
    const api = mainApiRef.current;
    if (!api?.chart) {
      setSynchronizedCrosshairX(null);
      return;
    }
    if (focusedMinuteMs == null) {
      setSynchronizedCrosshairX(null);
      return;
    }
    const time = Math.floor(focusedMinuteMs / 1000);
    const price = priceAtChartTime(lineData, time);
    let visible = null;
    try { visible = api.chart.timeScale().getVisibleRange?.(); } catch { /* no data yet */ }
    if (!Number.isFinite(price)
      || !visible
      || time < Number(visible.from)
      || time > Number(visible.to)) {
      setSynchronizedCrosshairX(null);
      return;
    }
    const x = api.chart.timeScale().timeToCoordinate?.(time);
    setSynchronizedCrosshairX(Number.isFinite(x) ? x + BOOKMAP_STRIKE_GUTTER_WIDTH : null);
  }, [focusSourceId, focusedMinuteMs, lineData, viewportVersion]);

  useEffect(() => {
    sharedWindowRef.current = sharedWindow;
    // Skip our own publish: the view already shows it, and re-resolving would
    // fight the gesture still in flight.
    if (!sharedWindow || publishedSharedWindowRef.current === sharedWindow) return;
    if (timeViewDetachedRef.current && sharedWindowSource === 'replay') return;
    updateTimeViewDetached(false);
    // Deliberately does NOT disarm auto-fit. This is a time-axis change; the
    // price scale is a separate concern, and disarming here froze the strike
    // range at mount so the price line drifted out of the pane.
    applySharedWindow();
  }, [applySharedWindow, sharedWindow, sharedWindowSource, updateTimeViewDetached]);

  useEffect(() => {
    if (!ready) return;
    applySharedPriceWindow();
  }, [
    applySharedPriceWindow,
    ready,
    sharedPriceWindow,
    sharedPriceWindowSource,
    snaps,
    spot,
  ]);

  useEffect(() => {
    if (!ready || !volumeChartRef.current) return undefined;
    const chart = createChart(volumeChartRef.current, buildChartOptions(
      volumeChartRef.current.clientWidth || 820,
      volumeChartRef.current.clientHeight || 150,
      false
    ));
    chart.applyOptions({
      crosshair: {
        vertLine: { visible: false, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      handleScroll: false,
      handleScale: false,
      timeScale: {
        visible: false,
      },
    });
    const histogram = chart.addSeries(HistogramSeries, {
      priceLineVisible: false,
      lastValueVisible: false,
      base: 0,
      color: 'rgba(0,0,0,0)',
    });
    // The histogram alone spans only `lineData`, while the main chart's logical
    // indices come from the full session grid. With a whole session loaded the
    // two happen to line up; at the open, where one bar is loaded, they do not
    // -- the candle sat at the left edge and its volume bar at the right. The
    // same hidden grid gives both charts one logical basis to share.
    const gridSeries = chart.addSeries(LineSeries, {
      color: 'rgba(0,0,0,0)',
      lineVisible: false,
      priceScaleId: 'time-grid',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });
    volumeApiRef.current = { chart, histogram, gridSeries };
    return () => {
      volumeApiRef.current = null;
      chart.remove();
    };
  }, [ready]);

  useEffect(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi) return;
    mainApi.chart.applyOptions({
      width: Math.max(1, mainSize.w - BOOKMAP_STRIKE_GUTTER_WIDTH),
      height: Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT),
    });
    // A resize keeps the logical range, so the visible times shift. Re-assert a
    // window that is still preference-owned rather than letting the layout pass
    // redefine it. Fit and overlay refresh wait until after series data exists.
    if (viewFromPreferenceRef.current) applySharedWindow();
  }, [applySharedWindow, mainSize]);

  useEffect(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi) return;
    mainApi.priceSeries.setData(candleData);
    const verticalMode = sharedWindowRef.current?.mode === WINDOW_MODE.SESSION
      ? WINDOW_MODE.SESSION
      : WINDOW_MODE.LATEST;
    const sessionDate = snaps.length ? etDateKey(snaps[snaps.length - 1].t) : 'none';
    const modeKey = verticalFitScopeKey(isReplaySession, sessionDate, verticalMode, layer);
    if (verticalModeKeyRef.current !== modeKey) {
      verticalModeKeyRef.current = modeKey;
      expandedVerticalBoundsRef.current = null;
    }
    const initialFit = autoFitRef.current
      ? computeVisibleFit(
        mainApi.chart,
        snaps,
        lineData,
        spot,
        mainSize.w,
        mainSize.h,
        layer,
        verticalMode === WINDOW_MODE.SESSION
      )
      : { rows, bounds: null };
    const expandedBounds = autoFitRef.current
      ? clampPriceBounds(
        expandOnlyPriceBounds(expandedVerticalBoundsRef.current, initialFit.bounds),
        initialFit.scaleCeiling
      )
      : null;
    const fittedRowBounds = autoFitRef.current
      ? clampPriceBounds(expandedBounds, initialFit.flowCeiling)
      : null;
    const computed = autoFitRef.current && expandedBounds
      ? {
          bounds: expandedBounds,
          rows: fittedRowBounds ? strikeRowsFromBounds(fittedRowBounds, spot) : initialFit.rows,
        }
      : initialFit;
    if (autoFitRef.current && computed.bounds) {
      expandedVerticalBoundsRef.current = computed.bounds;
    }
    const primitiveRows = computed.rows?.length ? computed.rows : rows;
    if (primitiveRows.length) rowsRef.current = primitiveRows;
    syncFitRows(setRows, primitiveRows);
    syncBoundarySeries(
      boundaryAppliedRef,
      mainApi,
      lineData,
      primitiveRows,
      computed.bounds
    );
    if (autoFitRef.current && primitiveRows.length) {
      lockPriceScaleToRows(
        mainApi.chart,
        primitiveRows,
        viewportRafRef,
        setViewportVersion,
        computed.bounds
      );
    }
    // Series updates can re-apply auto-scale after the shared-store effect ran.
    // Re-assert the stored intent after the new hydration bounds are installed.
    if (sharedPriceWindowRef.current) applySharedPriceWindow();
    if (primitives) {
      const primitive = primitiveRef.current;
      primitive?.setData({
        snaps,
        rows: primitiveRows,
        layer,
        palette,
        dots,
        flowSnaps: observedFlowSnaps,
        minVolume: volumeDotMinVolume,
        plotTop: 0,
        plotBottom: plotGeometry(mainApi.chart, mainSizeRef.current.w, mainSizeRef.current.h).bottom,
      });
      setPrimitiveBubbleSelection((selection) => {
        if (!selection || !primitive) return selection;
        const current = primitive.bubbleForId(selection.id)
          ?? primitive.bubbleAtChartTime(selection.time);
        return current ? { id: current.id, time: toChartTime(current.t) } : null;
      });
    }

    const fitKey = lineData.length ? `${lineData[0].time}-${lineData[lineData.length - 1].time}` : '';
    if (fitKey && fitKey !== fittedKeyRef.current) {
      if (timeViewDetachedRef.current) {
        if (autoFitRef.current) applyAutoFit();
        fittedKeyRef.current = fitKey;
        return;
      }
      // Data is ready now, so this is the point where stored intent can be
      // resolved. It outranks the automatic rolling-three-hour fit, which
      // remains the fallback when nothing applicable is stored.
      //
      // Only re-resolve when the chart follows the data or still shows the
      // resolved window. A selector-derived view remains authoritative.
      const mayReapply = viewFromPreferenceRef.current;
      if (mayReapply && applySharedWindow()) {
        fittedKeyRef.current = fitKey;
        return;
      }
      if (autoFitRef.current) {
        fitCharts();
        return;
      }
    }
    fittedKeyRef.current = fitKey;
  }, [applyAutoFit, applySharedPriceWindow, applySharedWindow, candleData, dots, fitCharts, isReplaySession, layer, lineData, mainSize, observedFlowSnaps, palette, primitives, rows, snaps, spot, volumeDotMinVolume]);

  useEffect(() => {
    const mainApi = mainApiRef.current;
    if (!mainApi) return;
    if (autoFitRef.current) applyAutoFit();
    const prevSize = prevMainSizeRef.current;
    const sizeChanged = Boolean(prevSize)
      && (prevSize.w !== mainSize.w || prevSize.h !== mainSize.h);
    prevMainSizeRef.current = { w: mainSize.w, h: mainSize.h };
    if (!sizeChanged) return;
    // Resize keeps the logical range, so lock-skip would otherwise leave
    // overlays on the pre-apply mapping. Settled refresh is for that layout
    // race, not for routine row updates — those keep the single-frame path.
    scheduleSettledViewportRefresh(viewportRafRef, setViewportVersion);
  }, [applyAutoFit, mainSize, ready]);

  useEffect(() => {
    const volApi = volumeApiRef.current;
    if (!volApi) return;
    volApi.chart.applyOptions({ width: volumeSize.w, height: volumeSize.h });
    syncVolumeTimeScale();
  }, [syncVolumeTimeScale, volumeSize]);

  useEffect(() => {
    mainApiRef.current?.timeScaleSeries.setData(timeScaleData);
    volumeApiRef.current?.gridSeries?.setData(timeScaleData);
    syncVolumeTimeScale();
  }, [syncVolumeTimeScale, timeScaleData]);

  useEffect(() => {
    if (!autoFit) return;
    applyAutoFit();
  }, [applyAutoFit, autoFit, layer, dots]);

  useEffect(() => {
    if (autoFit) return;
    if (useChartPriceWindowStore.getState().preference) return;
    applyCustomVerticalRange();
  }, [applyCustomVerticalRange, autoFit, lineData, snaps, spot]);

  useEffect(() => {
    const volApi = volumeApiRef.current;
    if (!volApi) return;
    volApi.histogram.setData(perMinNetVol);
    syncVolumeTimeScale();
  }, [perMinNetVol, syncVolumeTimeScale]);

  useEffect(() => {
    const canvas = volumeCanvasRef.current;
    const volApi = volumeApiRef.current;
    const mainApi = mainApiRef.current;
    if (!canvas || !volApi || !mainApi || !flowSnaps.length) return undefined;

    let raf = requestAnimationFrame(() => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
      const { w: W, h: H } = volumeSize;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      const mainChart = mainApi.chart;
      const mainWidth = Math.max(1, mainSizeRef.current.w || W);
      const mainHeight = mainSizeRef.current.h || H;
      const widthScale = W / mainWidth;
      const mainPlot = plotGeometry(mainChart, mainWidth, mainHeight);
      const topPad = 28;
      const bottomPad = 18;
      const left = clamp(mainPlot.left * widthScale, 28, Math.max(28, W - 2));
      const right = Math.max(left + 1, Math.min(W - 1, mainPlot.right * widthScale || W - 1));
      const top = topPad;
      const bottom = Math.max(top + 1, H - bottomPad);
      const gridW = right - left;
      const gridH = bottom - top;
      if (gridW <= 4 || gridH <= 4) return;

      const valueByTime = new Map(
        perMinNetVol
          .filter((entry) => Number.isFinite(entry.value))
          .map((entry) => [entry.time * 1000, entry.value])
      );
      const unscaledColumns = buildMinuteColumns(snaps, mainChart, mainWidth, mainHeight)
        .filter((entry) => !entry.offPlot);
      // Kept unscaled (in the main chart's own coordinates) so selection and
      // projection match the main axis exactly before this pane rescales x.
      const timeAxisColumns = buildTimeAxisColumns(
        snaps,
        mainChart,
        mainWidth,
        mainHeight,
        mainPlot,
        timeScaleData
      );
      const columns = unscaledColumns
        .map((entry) => ({
          ...entry,
          left: clamp(entry.left * widthScale, left, right),
          right: clamp(entry.right * widthScale, left, right),
          rawLeft: entry.rawLeft * widthScale,
          rawRight: entry.rawRight * widthScale,
          x: entry.x * widthScale,
          value: valueByTime.get(entry.t),
        }))
        .filter((entry) => entry.right > left && entry.left < right);

      const maxAbs = sessionAnchoredVolumeMax(perMinNetVol);
      const usableHalf = Math.max(16, gridH / 2 - 14);
      const zeroY = top + gridH / 2;
      const scale = usableHalf / maxAbs;
      const focusedTime = minuteBucketMs(crosshairHover?.t ?? null);
      const focusedColumn = focusedTime ? columns.find((entry) => entry.t === focusedTime) : null;
      const peakTime = volumeSummary.peak?.t ?? null;

      ctx.fillStyle = 'rgba(245,248,252,0.028)';
      ctx.fillRect(left, top, gridW, gridH);

      if (focusedColumn) {
        // Clamped to the bar's own width rule. A column bordering a break in the
        // snapshot series spans the whole break, and painting that unclamped
        // highlighted tens of minutes as though they were the hovered minute.
        const highlightW = volumeColumnBarWidth(focusedColumn) + 4;
        const mid = focusedColumn.x;
        ctx.save();
        ctx.beginPath();
        ctx.rect(left, top, gridW, gridH);
        ctx.clip();
        ctx.fillStyle = 'rgba(244,194,43,0.09)';
        ctx.fillRect(mid - highlightW / 2, top, highlightW, gridH);
        ctx.restore();
      }

      ctx.fillStyle = BOOKMAP_NET_FLOW_COLORS.positive(0.035);
      ctx.fillRect(left, top, gridW, zeroY - top);
      ctx.fillStyle = BOOKMAP_NET_FLOW_COLORS.negative(0.04);
      ctx.fillRect(left, zeroY, gridW, bottom - zeroY);

      for (const value of volumeAxisTicks(maxAbs)) {
        const y = zeroY - value * scale;
        if (y < top || y > bottom) continue;
        ctx.strokeStyle = value === 0 ? 'rgba(245,248,252,0.28)' : 'rgba(245,248,252,0.10)';
        ctx.lineWidth = value === 0 ? 1.1 : 1;
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();

        ctx.fillStyle = value < 0 ? BOOKMAP_NET_FLOW_COLORS.negative(0.82) : value > 0 ? BOOKMAP_NET_FLOW_COLORS.positive(0.84) : 'rgba(245,248,252,0.74)';
        ctx.font = "10px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(fmtVolumeAxis(value), left - 2, y);
      }

      ctx.fillStyle = 'rgba(245,248,252,0.58)';
      ctx.font = "10px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('Net Vol', 6, 15);

      if (volumeSummary.latest) {
        ctx.fillStyle = volumeSummary.latest.value >= 0 ? BOOKMAP_NET_FLOW_COLORS.positive(0.92) : BOOKMAP_NET_FLOW_COLORS.negative(0.92);
        ctx.textAlign = 'right';
        ctx.fillText(`Last ${fmtCompact(volumeSummary.latest.value)}`, W - 6, 15);
      }
      if (volumeSummary.peak) {
        ctx.fillStyle = 'rgba(245,248,252,0.64)';
        ctx.textAlign = 'left';
        ctx.fillText(`Peak ${etTime(volumeSummary.peak.t)} ${fmtCompact(volumeSummary.peak.value)}`, 92, 15);
      }

      ctx.save();
      ctx.beginPath();
      ctx.rect(left, top, gridW, gridH);
      ctx.clip();
      for (const entry of columns) {
        if (!Number.isFinite(entry.value)) continue;
        const mid = entry.x;
        const barW = volumeColumnBarWidth(entry);
        const height = Math.max(1, Math.abs(entry.value) * scale);
        const x = mid - barW / 2;
        const y = entry.value >= 0 ? zeroY - height : zeroY;
        const isPeak = peakTime != null && entry.t === peakTime;
        ctx.fillStyle = entry.value >= 0 ? BOOKMAP_NET_FLOW_COLORS.positive(0.88) : BOOKMAP_NET_FLOW_COLORS.negative(0.9);
        ctx.fillRect(x, y, barW, height);
        if (isPeak) {
          ctx.strokeStyle = 'rgba(245,248,252,0.78)';
          ctx.lineWidth = 1;
          ctx.strokeRect(x - 1, y - (entry.value >= 0 ? 1 : 0), barW + 2, height + 1);
        }
      }
      ctx.restore();

      // Same projected columns and adaptive selector as the main axis; only
      // the returned x is scaled into this pane's band.
      let timeLabels = selectTimeAxisLabels({
        columns: timeAxisColumns,
        plotWidthPx: mainPlot.right - mainPlot.left,
      });
      timeLabels = includeHighlightedTimeAxisLabel(timeLabels, timeAxisColumns, focusedTime);
      ctx.font = "10px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (const label of timeLabels) {
        const isFocused = focusedTime != null && label.t === focusedTime;
        ctx.fillStyle = isFocused ? PRICE_LINE_COLOR : 'rgba(245,248,252,0.56)';
        const x = clamp(label.x * widthScale, left + 22, right - 22);
        ctx.fillText(etTime(label.t), x, H - 14);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [crosshairHover, flowSnaps, lineData, perMinNetVol, snaps, timeScaleData, volumeSize, volumeSummary, viewportVersion, windowContext]);

  useEffect(() => {
    if (primitives) return undefined;
    const canvas = mainOverlayRef.current;
    const mainApi = mainApiRef.current;
    if (!canvas) return undefined;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
    canvas.width = Math.round(mainSize.w * dpr);
    canvas.height = Math.round(mainSize.h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, mainSize.w, mainSize.h);
    if (!mainApi || !rows.length || !snaps.length) return undefined;

    const cells = buildHeatCells(
      snaps,
      rows,
      layer,
      palette,
      mainApi.chart,
      mainApi.priceSeries,
      mainSize.w,
      mainSize.h
    );
    for (const cell of cells) {
      ctx.fillStyle = cell.color;
      ctx.fillRect(cell.left, cell.top, cell.width, cell.height);
    }
  }, [layer, mainSize, palette, primitives, rows, snaps, viewportVersion]);

  useEffect(() => {
    if (!dots || primitives) {
      setDisplayedDots([]);
      return;
    }
    const mainApi = mainApiRef.current;
    if (!mainApi) return;
    setDisplayedDots(
      buildDisplayedDots(
        observedFlowSnaps,
        mainApi.chart,
        mainApi.priceSeries,
        mainSize.w,
        mainSize.h,
        volumeDotMinVolume
      )
    );
  }, [dots, mainSize, observedFlowSnaps, primitives, viewportVersion, volumeDotMinVolume]);

  useEffect(() => {
    if (primitives) return undefined;
    const canvas = gexOverlayRef.current;
    const mainApi = mainApiRef.current;
    if (!canvas || !mainApi) return;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
    canvas.width = Math.round(mainSize.w * dpr);
    canvas.height = Math.round(mainSize.h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, mainSize.w, mainSize.h);

    const plot = plotGeometry(mainApi.chart, mainSize.w, mainSize.h);
    if (zeroGexCrossings.length && rows.length) {
      const minStrike = rows[0];
      const maxStrike = rows[rows.length - 1];
      const markers = zeroGexCrossings
      .map((crossing) => {
        const snap = snaps[crossing.snapIdx];
        const chartTime = snap ? toChartTime(snap.t) : null;
        const chartX = chartTime != null ? mainApi.chart.timeScale().timeToCoordinate(chartTime) : null;
        // The crossing is a gamma-flip in time; sit the diamond on the SPX candle-close path
        // at that time. The candle source is the SPX index, not chain spot, so read the close helper
        // (crossing.price is chain spot - would float the diamond off the line).
        const linePrice = priceAtChartTime(lineData, chartTime);
        const price = clamp(Number.isFinite(linePrice) ? linePrice : crossing.price, minStrike, maxStrike);
        const y = mainApi.priceSeries.priceToCoordinate(price);
        if (!Number.isFinite(chartX) || !Number.isFinite(y)) return null;
        return {
          ...crossing,
          x: plot.left + chartX,
          y: clamp(y, plot.top, plot.bottom),
        };
      })
      .filter(Boolean);
      if (markers.length) {

        ctx.save();
        ctx.beginPath();
        ctx.rect(plot.left, plot.top, plot.right - plot.left, plot.bottom - plot.top);
        ctx.clip();

        ctx.strokeStyle = 'rgba(245,248,252,0.82)';
        ctx.lineWidth = 1.25;
        ctx.setLineDash([6, 5]);
        for (let index = 0; index < markers.length; index += 1) {
          const marker = markers[index];
          const next = markers[index + 1];
          const x2 = next ? next.x : plot.right;
          ctx.beginPath();
          ctx.moveTo(marker.x, marker.y);
          ctx.lineTo(x2, marker.y);
          ctx.stroke();
        }
        ctx.setLineDash([]);

        for (const marker of markers) {
          const s = 5;
          ctx.save();
          ctx.translate(marker.x, marker.y);
          ctx.rotate(Math.PI / 4);
          ctx.fillStyle = 'rgba(5,8,12,0.92)';
          ctx.fillRect(-s, -s, s * 2, s * 2);
          ctx.strokeStyle = 'rgba(245,248,252,0.98)';
          ctx.lineWidth = 1.25;
          ctx.strokeRect(-s, -s, s * 2, s * 2);
          ctx.restore();
        }
        ctx.restore();

        const latestMarker = markers[markers.length - 1];
        ctx.fillStyle = 'rgba(245,248,252,0.9)';
        ctx.font = "11px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
        ctx.textAlign = 'left';
        ctx.fillText(
          `0-GEX ~${Math.round(latestZeroGex.price)}`,
          Math.min(plot.right - 76, latestMarker.x + 8),
          clamp(latestMarker.y - 8, plot.top + 10, plot.bottom - 4)
        );
      }
    }

  }, [latestZeroGex, lineData, mainSize, primitives, rows, snaps, viewportVersion, zeroGexCrossings]);

  useEffect(() => {
    const canvas = axisOverlayRef.current;
    const mainApi = mainApiRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
    canvas.width = Math.round(mainSize.w * dpr);
    canvas.height = Math.round(mainSize.h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, mainSize.w, mainSize.h);
    if (!mainApi || !priceLabelValues.length || !snaps.length) return;

    drawLeftStrikeLabels(
      ctx,
      priceLabelValues,
      mainApi.chart,
      mainApi.priceSeries,
      mainSize.w,
      mainSize.h,
      strikeLabelStep
    );
    drawBottomTimeLabels(
      ctx,
      snaps,
      mainApi.chart,
      mainSize.w,
      mainSize.h,
      minuteBucketMs(crosshairHover?.t ?? null),
      timeScaleData
    );
  }, [crosshairHover?.t, mainSize, priceLabelValues, snaps, strikeLabelStep, timeScaleData, viewportVersion]);

  useEffect(() => {
    const canvas = histCanvasRef.current;
    if (!canvas || !rows.length || !snaps.length) return;
    const latest = snaps[snaps.length - 1];
    if (!latest) return;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_RENDER_DPR);
    const width = histSize.w;
    const height = histSize.h;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const rowsDesc = rows.slice().reverse();
    const title = layer === 'volume' ? 'Net Vol' : layer === 'delta' ? 'Net Delta' : 'Net Gamma';
    const histStyle = BOOKMAP_LC_VISUALS.histogram;
    const gridLeft = histStyle.left;
    const gridRight = width - histStyle.rightPad;
    const mainChart = mainApiRef.current?.chart;
    const mainPlot = mainChart ? plotGeometry(mainChart, mainSize.w, mainSize.h) : null;
    const gridTop = 0;
    const gridBottom = mainPlot
      ? clamp(mainPlot.bottom * (height / Math.max(1, mainSize.h)), 1, height - 1)
      : Math.max(1, height - histStyle.bottomPad);
    const gridWidth = gridRight - gridLeft;
    const gridHeight = gridBottom - gridTop;
    if (gridWidth <= 0 || gridHeight <= 0) return;

    const rowHeight = gridHeight / rowsDesc.length;
    const zeroX = gridLeft + gridWidth / 2;
    const values = rowsDesc.map((strike) => cellValueAt(latest, strike, layer));
    const maxAbs = Math.max(...values.map((value) => Math.abs(value)), 1);
    const scale = (gridWidth / 2 - 8) / maxAbs;

    // Map strikes through the main chart's price scale so rows land at the same screen-y
    // as the heatmap (the panels are top-aligned and the heatmap overlay uses these same
    // coordinates).
    const priceToY = (strike) => {
      const yy = mainApiRef.current?.priceSeries?.priceToCoordinate?.(strike);
      return Number.isFinite(yy) ? yy : null;
    };
    const haveScale = priceToY(rowsDesc[0]) != null;
    const yForStrike = (strike, index) => {
      const yy = priceToY(strike);
      return yy != null ? yy : gridTop + index * rowHeight + rowHeight / 2;
    };
    const rowPx = (haveScale && rows.length > 1
      && Math.abs((priceToY(rows[1]) ?? 0) - (priceToY(rows[0]) ?? 0))) || rowHeight;
    const bandTop = haveScale ? (priceToY(rowsDesc[0]) ?? gridTop) : gridTop;
    const bandBottom = haveScale ? (priceToY(rowsDesc[rowsDesc.length - 1]) ?? gridBottom) : gridBottom;

    ctx.strokeStyle = histStyle.gridColor;
    ctx.setLineDash([5, 5]);
    for (const tick of [-maxAbs, -maxAbs / 2, 0, maxAbs / 2, maxAbs]) {
      const x = zeroX + tick * scale;
      ctx.beginPath();
      ctx.moveTo(x, bandTop);
      ctx.lineTo(x, bandBottom);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.strokeStyle = histStyle.centerLineColor;
    ctx.lineWidth = histStyle.centerLineWidth;
    ctx.beginPath();
    ctx.moveTo(zeroX, bandTop);
    ctx.lineTo(zeroX, bandBottom);
    ctx.stroke();
    ctx.lineWidth = 1;

    ctx.fillStyle = 'rgba(245,248,252,0.72)';
    ctx.font = histStyle.strikeFont;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    // Locked to the price axis's step so the two halves of the tile agree.
    const rowIndex = new Map(rowsDesc.map((strike, index) => [strike, index]));
    const strikeLabels = selectStrikeLabels({
      strikes: priceLabelValues,
      step: strikeLabelStep,
      toCoordinate: (strike) => yForStrike(strike, rowIndex.get(strike)),
      plotTop: gridTop,
      plotBottom: gridBottom,
    });
    for (const label of strikeLabels) {
      ctx.fillText(String(label.strike), gridLeft - 8, label.y);
    }

    for (let index = 0; index < rowsDesc.length; index += 1) {
      const value = values[index];

      if (!Number.isFinite(value) || value === 0) {
        continue;
      }

      const y = yForStrike(rowsDesc[index], index);

      const barWidth = Math.max(
        1,
        Math.abs(value) * scale
      );

      const barHeight = clamp(
        rowPx * histStyle.barFillRatio,
        histStyle.barMinHeight,
        histStyle.barMaxHeight
      );

      const normalizedValue = bucketNormalizedValue(
        value,
        layer
      );

      const { rgb, alpha } = bookmapHeatBandColor(
        normalizedValue,
        palette,
        layer
      );

      ctx.fillStyle = rgbTuple(rgb, alpha);

      ctx.fillRect(
        value >= 0 ? zeroX : zeroX - barWidth,
        y - barHeight / 2,
        barWidth,
        barHeight
      );
    }

    if (Number.isFinite(spot)) {
      const rawY = priceToY(spot);
      const y = Number.isFinite(rawY) ? rawY : gridTop + gridHeight / 2;
      ctx.strokeStyle = 'rgba(245,248,252,0.58)';
      ctx.setLineDash([6, 5]);
      ctx.beginPath();
      ctx.moveTo(gridLeft, y);
      ctx.lineTo(gridRight, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = PRICE_LINE_COLOR;
      ctx.fillRect(gridLeft, y - 10, 68, 20);
      ctx.fillStyle = '#06090d';
      ctx.font = "12px 'SFMono-Regular', Consolas, 'Liberation Mono', monospace";
      ctx.textAlign = 'center';
      ctx.fillText(formatWholePrice(spot), gridLeft + 34, y + 4);
    }

    // Same band centre as the main chart's time labels (drawBottomTimeLabels),
    // so the title, the value ticks, and the time axis all sit on one baseline.
    const axisHeight = height - gridBottom;
    const tickY = gridBottom + axisHeight / 2 + 1;
    const tickValues = [-maxAbs, -maxAbs / 2, 0, maxAbs / 2, maxAbs];
    ctx.textBaseline = 'middle';
    ctx.fillStyle = histStyle.titleColor;
    ctx.font = histStyle.titleFont;
    ctx.textAlign = 'left';
    ctx.fillText(title, 1, tickY);
    ctx.font = histStyle.tickFont;
    tickValues.forEach((tick, index) => {
      const x = zeroX + tick * scale;
      // Edge-align the outermost ticks so the leftmost stops overhanging into
      // the strike gutter (and the title's space) and the rightmost stops
      // bleeding into rightPad.
      ctx.textAlign = index === 0 ? 'left' : index === tickValues.length - 1 ? 'right' : 'center';
      ctx.fillStyle = tick < 0 ? histStyle.negativeText : tick > 0 ? histStyle.positiveText : histStyle.neutralText;
      ctx.fillText(fmtCompact(tick), x, tickY);
    });
  }, [histSize, layer, mainSize, palette, priceLabelValues, rows, snaps, spot, strikeLabelStep, viewportVersion]);

  // Bubble in the hovered time column (dots are pointer-events:none so the LC
  // crosshair tracks the cursor uniformly whether or not it is over a bubble).
  const bubbleHover = useMemo(() => {
    if (primitives) {
      if (!primitiveBubbleSelection) return null;
      const bubble = primitiveRef.current?.bubbleForId(primitiveBubbleSelection.id)
        ?? primitiveRef.current?.bubbleAtChartTime(primitiveBubbleSelection.time)
        ?? null;
      return bubble ? { ...bubble, cx: bubble.cx + BOOKMAP_STRIKE_GUTTER_WIDTH } : null;
    }
    if (!crosshairHover?.t) return null;
    return displayedDots.find((dot) => dot.t === crosshairHover.t) || null;
  }, [crosshairHover?.t, displayedDots, primitiveBubbleSelection, primitives]);
  const bubbleFirst = Boolean(
    bubbleHover
    && Number.isFinite(bubbleHover.cy)
    && Number.isFinite(crosshairHover?.y)
    && bubbleHover.cy <= crosshairHover.y
  );
  const minuteSummaryFlow = crosshairHover?.t
    ? observedGrossVolumeMap.get(toChartTime(crosshairHover.t))
    : null;
  const minuteSummaryGrossVolume = Number.isFinite(minuteSummaryFlow?.value)
    ? minuteSummaryFlow.value
    : null;
  const minuteSummaryNetVolume = crosshairHover?.t
    ? perMinNetVol.find((point) => point.time === toChartTime(crosshairHover.t))?.value
    : null;
  const minuteSummaryClosePrice = crosshairHover?.t
    ? priceAtChartTime(lineData, toChartTime(crosshairHover.t))
    : null;

  // Measured tooltip box so the follow/clamp math knows the real size. Re-runs
  // when the hovered content changes; the equality guard prevents update loops.
  useLayoutEffect(() => {
    const el = tipStackRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setTipSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, [
    bubbleHover,
    crosshairHover,
    layer,
    minuteSummaryClosePrice,
    minuteSummaryFlow?.gapMinutes,
    minuteSummaryFlow?.recoveryGap,
    minuteSummaryGrossVolume,
    minuteSummaryNetVolume,
  ]);

  const tipRegion = useMemo(() => {
    const chart = mainApiRef.current?.chart;
    if (!chart) return { left: 0, right: mainSize.w, top: 0, bottom: mainSize.h };
    return plotGeometry(chart, mainSize.w, mainSize.h);
    // viewportVersion bumps whenever the LC scales change, so the plot rect refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainSize, viewportVersion]);

  // Reflect what the chart is actually showing, not merely what is stored. A
  // stored `absolute` interval from another session can fail to resolve here --
  // `applySharedWindow` returns false and the view falls back to the rolling
  // default -- and the segment must not claim `Custom` over a `Latest` view.
  // No stored preference is likewise a rolling, latest-anchored view.
  const activeTimeMode = useMemo(() => {
    if (timeViewDetached) return WINDOW_MODE.ABSOLUTE;
    const mode = isCustomWindowPreference(sharedWindow)
      ? WINDOW_MODE.ABSOLUTE
      : sharedWindow?.mode;
    if (!mode) return WINDOW_MODE.LATEST;
    // The originating chart already displays this exact gesture. Keep its
    // status truthful even when an intentional right gutter extends beyond a
    // very short available data extent.
    if (publishedSharedWindowRef.current === sharedWindow) return mode;
    // Nothing to resolve against yet; the stored intent is what will be applied.
    if (!lineData.length) return mode;
    // Derived from `lineData` rather than `windowContext()`, which reads a ref
    // written in an effect and so trails this render by one pass.
    const times = lineData.map((point) => point.time);
    const extent = clampExtentToSessionClose(extentFromEpochSeconds(times));
    if (!extent) return mode;
    const { status } = resolveWindowPreference(sharedWindow, extent, {
      barIntervalSeconds: inferBarIntervalSeconds(times),
    });
    return status === RESOLUTION_STATUS.UNAVAILABLE ? WINDOW_MODE.LATEST : mode;
  }, [lineData, sharedWindow, timeViewDetached]);

  // A single bar spans no time, so no mode can produce a valid range and every
  // click would be a silent no-op. The segments are a readout as much as a
  // control, so they go inert rather than accepting a click that does nothing.
  const timeModesActionable = lineData.length > 1;
  const commitBasisDraft = () => {
    const next = Number(basisDraft);
    if (!Number.isFinite(next)) {
      setBasisDraft(String(esSyntheticBasis ?? 0));
      return;
    }
    setEsSyntheticBasis(next);
  };
  const handleBasisKeyDown = (event) => {
    if (event.key === 'Enter') event.currentTarget.blur();
    if (event.key === 'Escape') {
      setBasisDraft(String(esSyntheticBasis ?? 0));
      event.currentTarget.blur();
    }
  };
  const applyPriceAxisWheelZoom = (deltaY) => {
    const mainApi = mainApiRef.current;
    const plotHeight = Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT);
    const current = currentVisiblePriceRange(mainApi?.chart, mainApi?.priceSeries, plotHeight);
    const next = clampManualPriceBounds(
      scalePriceAxisRange(current, deltaY, plotHeight),
      manualPriceCeiling(snapsRef.current, spotRef.current)
    );
    if (!mainApi || !next) return false;

    markUserViewportIntent();
    disarmAutoFit();
    mainApi.chart.priceScale('left').setAutoScale(false);
    mainApi.chart.priceScale('left').setVisibleRange(next);
    setCustomVerticalSpan(next.to - next.from);
    syncManualPriceRows(next);
    publishPriceRange(next);
    scheduleViewportRefresh(viewportRafRef, setViewportVersion);
    return true;
  };
  const handleWheel = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const hasPointerPosition = event.clientX !== 0 || event.clientY !== 0;
    const priceAxis = hasPointerPosition
      && event.clientX - bounds.left <= BOOKMAP_STRIKE_GUTTER_WIDTH;
    const timeAxis = hasPointerPosition
      && !priceAxis
      && event.clientY - bounds.top >= mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT;
    if (priceAxis) {
      const plotHeight = Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT);
      applyPriceAxisWheelZoom(normalizeWheelDelta(event, plotHeight));
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (timeAxis) {
      const timeScale = mainApiRef.current?.chart.timeScale();
      const current = timeScale?.getVisibleLogicalRange();
      const scaleData = timeScaleDataRef.current;
      const sessionOpen = scaleData.length
        ? timeToLogical(
          scaleData,
          DEFAULT_BAR_INTERVAL_SECONDS,
          sessionOpenEpochSeconds(scaleData[0].time * 1000)
        )
        : null;
      const sessionClose = sessionCloseLogical(scaleData);
      const limits = Number.isFinite(sessionOpen) && Number.isFinite(sessionClose)
        ? { sessionOpen, sessionClose }
        : null;
      const next = clampVisibleLogicalRange(
        scaleTimeAxisRange(
          current,
          normalizeWheelDelta(event, mainSize.w),
          mainSize.w,
          limits
        )
      );
      markUserTimeZoomIntent();
      if (next) {
        timeScale.setVisibleLogicalRange(next);
        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      }
      scheduleInteractiveTimeCorrection(600);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // The wheel zooms the time scale on both variants, so time intent opens on
    // both or the primitives tab could never publish. It must not disarm
    // price auto-fit: after Auto was removed, a time-only wheel would freeze
    // the price axis until remount.
    markUserTimeZoomIntent();
    scheduleInteractiveTimeCorrection(600);
  };
  const handleTouchZoom = (event) => {
    if (event.touches.length < 2) return;
    touchZoomActiveRef.current = true;
    markUserTimeZoomIntent();
    scheduleInteractiveTimeCorrection(600);
  };
  const handleTouchZoomEnd = (event) => {
    if (!touchZoomActiveRef.current) return;
    if (event.touches.length >= 2) {
      markUserTimeZoomIntent();
      return;
    }
    touchZoomActiveRef.current = false;
    if (event.touches.length === 1 && dragStartRef.current) {
      const [touch] = event.touches;
      const timeAxis = dragStartRef.current.timeAxis;
      dragStartRef.current = {
        ...dragStartRef.current,
        x: touch.clientX,
        y: touch.clientY,
        timeRange: mainApiRef.current?.chart.timeScale().getVisibleLogicalRange() || null,
        priceRange: !timeAxis
          ? currentVisiblePriceRange(
              mainApiRef.current?.chart,
              mainApiRef.current?.priceSeries,
              Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT)
            )
          : null,
        active: false,
        timePan: false,
        verticalPan: false,
      };
    }
    scheduleInteractiveTimeCorrection();
  };
  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    if (event.pointerType === 'touch') {
      activeTouchPointerIdsRef.current.add(event.pointerId);
    }
    if (dragStartRef.current) return;
    if (verticalEaseRafRef.current) {
      window.cancelAnimationFrame(verticalEaseRafRef.current);
      verticalEaseRafRef.current = null;
    }
    const resumeTimeCorrection = Boolean(timeGestureSettleTimerRef.current);
    if (resumeTimeCorrection) {
      window.clearTimeout(timeGestureSettleTimerRef.current);
      timeGestureSettleTimerRef.current = null;
    }
    programmaticRangeUntilRef.current = 0;
    const bounds = event.currentTarget.getBoundingClientRect();
    const priceAxis = event.clientX - bounds.left <= BOOKMAP_STRIKE_GUTTER_WIDTH;
    const timeAxis = !priceAxis
      && event.clientY - bounds.top >= mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT;
    if (!timeAxis) userTimeZoomUntilRef.current = 0;
    const timeRange = mainApiRef.current?.chart.timeScale().getVisibleLogicalRange() || null;
    const priceRange = !timeAxis
      ? currentVisiblePriceRange(
        mainApiRef.current?.chart,
        mainApiRef.current?.priceSeries,
        Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT)
      )
      : null;
    const scaleData = timeScaleDataRef.current;
    if (timeAxis && !scaleData.length) return;
    // The session scale guarantees exactly one logical bar per minute.
    const sessionOpen = scaleData.length
      ? timeToLogical(
        scaleData,
        DEFAULT_BAR_INTERVAL_SECONDS,
        sessionOpenEpochSeconds(scaleData[0].time * 1000)
      )
      : null;
    const sessionClose = sessionCloseLogical(scaleData);
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      priceAxis,
      timeAxis,
      timeRange,
      priceRange,
      pointerType: event.pointerType,
      pointerId: event.pointerId,
      resumeTimeCorrection,
      timeLimits: Number.isFinite(sessionOpen) && Number.isFinite(sessionClose)
        ? { sessionOpen, sessionClose }
        : null,
    };
  };
  const handlePointerMove = (event) => {
    if (!dragStartRef.current || (event.buttons & 1) !== 1) return;
    const touchPointer = dragStartRef.current.pointerType === 'touch'
      || event.pointerType === 'touch';
    const dx = Math.abs(event.clientX - dragStartRef.current.x);
    const dy = Math.abs(event.clientY - dragStartRef.current.y);
    if (touchPointer && touchZoomActiveRef.current) return;
    if (!dragStartRef.current.active
      && dx < PLOT_PAN_THRESHOLD_PX
      && dy < PLOT_PAN_THRESHOLD_PX) return;

    // Re-marked on every qualifying move, not once per drag: the window is only
    // 600ms, so a slower drag used to publish its mid-drag range and lose the
    // position the user actually released at. `clearPointerDrag` ends the drag.
    if (dragStartRef.current.timeAxis) markUserTimeZoomIntent();

    if (dragStartRef.current.timeAxis) {
      if (!dragStartRef.current.active) dragStartRef.current.active = true;
      const nextRange = scaleTimeAxisRange(
        dragStartRef.current.timeRange,
        event.clientX - dragStartRef.current.x,
        mainSize.w,
        dragStartRef.current.timeLimits
      );
      const visible = clampVisibleLogicalRange(nextRange);
      if (visible) {
        mainApiRef.current?.chart.timeScale().setVisibleLogicalRange(visible);
        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      }
      event.preventDefault();
      return;
    }

    if (dragStartRef.current.priceAxis) {
      const mainApi = mainApiRef.current;
      const startRange = dragStartRef.current.priceRange;
      if (!mainApi || !startRange) return;

      markUserViewportIntent();
      if (!dragStartRef.current.active) {
        dragStartRef.current.active = true;
        disarmAutoFit();
      }

      const scaledRange = scalePriceAxisRange(
        startRange,
        event.clientY - dragStartRef.current.y,
        Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT)
      );
      const nextRange = clampManualPriceBounds(
        scaledRange,
        manualPriceCeiling(snapsRef.current, spotRef.current)
      );

      if (nextRange) {
        const priceScale = mainApi.chart.priceScale('left');
        priceScale.setAutoScale(false);
        priceScale.setVisibleRange(nextRange);
        setCustomVerticalSpan(nextRange.to - nextRange.from);

        // Manual Y scaling changes the viewport, not the available strike
        // universe. Rebuild the visible 5-point rows from the new price range
        // and clamp them to the strikes actually hydrated by the feed.
        syncManualPriceRows(nextRange);

        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      }

      event.preventDefault();
      return;
    }

    const mainApi = mainApiRef.current;
    if (!mainApi) return;
    if (dx >= PLOT_PAN_THRESHOLD_PX) {
      if (!dragStartRef.current.timePan) updateTimeViewDetached(true);
      dragStartRef.current.timePan = true;
      markUserTimeCustomIntent();
    }
    if (dy >= PLOT_PAN_THRESHOLD_PX && dragStartRef.current.priceRange) {
      markUserViewportIntent();
      if (!dragStartRef.current.verticalPan) {
        dragStartRef.current.verticalPan = true;
        disarmAutoFit();
      }
      const nextRange = translatePriceRange(
        dragStartRef.current.priceRange,
        event.clientY - dragStartRef.current.y,
        Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT),
        manualPriceCeiling(snapsRef.current, spotRef.current)
      );
      if (nextRange) {
        const priceScale = mainApi.chart.priceScale('left');
        priceScale.setAutoScale(false);
        priceScale.setVisibleRange(nextRange);
        syncManualPriceRows(nextRange);
        scheduleViewportRefresh(viewportRafRef, setViewportVersion);
      }
    }
    dragStartRef.current.active = true;
    event.preventDefault();

  };
  const clearPointerDrag = useCallback(() => {
    const hadVerticalGesture = Boolean(
      dragStartRef.current?.priceAxis || dragStartRef.current?.verticalPan
    );
    if (
      dragStartRef.current?.timeAxis
      || dragStartRef.current?.timePan
      || dragStartRef.current?.resumeTimeCorrection
    ) {
      scheduleInteractiveTimeCorrection();
    }
    dragStartRef.current = null;
    if (hadVerticalGesture && !autoFitRef.current) {
      publishPriceRange(currentVisiblePriceRange(
        mainApiRef.current?.chart,
        mainApiRef.current?.priceSeries,
        Math.max(1, mainSizeRef.current.h - BOOKMAP_TIME_AXIS_HEIGHT)
      ));
    }
  }, [publishPriceRange, scheduleInteractiveTimeCorrection]);

  const handlePointerRelease = useCallback((event) => {
    if (event.type === 'blur') {
      activeTouchPointerIdsRef.current.clear();
      clearPointerDrag();
      return;
    }

    if (event.pointerType === 'touch') {
      activeTouchPointerIdsRef.current.delete(event.pointerId);
    }
    const activePointerId = dragStartRef.current?.pointerId;
    if (
      touchZoomActiveRef.current
      && activeTouchPointerIdsRef.current.size > 0
    ) {
      if (activePointerId === event.pointerId && dragStartRef.current) {
        dragStartRef.current.pointerId = activeTouchPointerIdsRef.current.values().next().value;
      }
      return;
    }
    if (
      activePointerId != null
      && event.pointerId !== activePointerId
    ) return;
    clearPointerDrag();
  }, [clearPointerDrag]);

  useEffect(() => {
    document.addEventListener('pointerup', handlePointerRelease, true);
    document.addEventListener('pointercancel', handlePointerRelease, true);
    window.addEventListener('blur', handlePointerRelease);
    return () => {
      document.removeEventListener('pointerup', handlePointerRelease, true);
      document.removeEventListener('pointercancel', handlePointerRelease, true);
      window.removeEventListener('blur', handlePointerRelease);
    };
  }, [handlePointerRelease]);

  const handleHistogramPointerDown = (event) => {
    if (event.button !== 0) return;
    if (dragStartRef.current) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const priceAxis = event.clientX - bounds.left <= BOOKMAP_HIST_PRICE_AXIS_HIT_WIDTH;
    if (!priceAxis) return;

    const priceRange = currentVisiblePriceRange(
      mainApiRef.current?.chart,
      mainApiRef.current?.priceSeries,
      Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT)
    );
    if (!priceRange) return;

    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      priceAxis: true,
      timeAxis: false,
      timeRange: null,
      priceRange,
      pointerId: event.pointerId,
      timeLimits: null,
      source: 'histogram',
    };

    // Keep receiving the drag even if the pointer crosses out of the narrow
    // histogram strike-label gutter.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const handleHistogramWheel = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (event.clientX - bounds.left > BOOKMAP_HIST_PRICE_AXIS_HIT_WIDTH) return;

    const plotHeight = Math.max(1, mainSize.h - BOOKMAP_TIME_AXIS_HEIGHT);
    applyPriceAxisWheelZoom(normalizeWheelDelta(event, plotHeight));
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDoubleClick = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const priceAxisWidth = event.currentTarget.classList.contains('bmpro-hist')
      ? BOOKMAP_HIST_PRICE_AXIS_HIT_WIDTH
      : BOOKMAP_STRIKE_GUTTER_WIDTH;
    const priceAxis = event.clientX - bounds.left <= priceAxisWidth;
    if (!priceAxis) return;

    const mainApi = mainApiRef.current;
    if (!mainApi) return;

    autoFitRef.current = true;
    resetPriceWindow(windowSourceId);
    setCustomVerticalSpan(null);
    expandedVerticalBoundsRef.current = null;
    lastLockedRangeByChart.delete(mainApi.chart);
    applyAutoFit();
    scheduleViewportRefresh(viewportRafRef, setViewportVersion);
    event.preventDefault();
  };

  return (
    <div className={`bmpro-panel ${palette === 'bookmap' ? 'bmpro-panel--bookmap' : ''}`}>
      <WidgetPanelLiveStatus dot />
      <WidgetPanelControls className="bmpro-header-controls">
        <label className="bmpro-select-control">
          <select
            value={layer}
            aria-label="Heat layer"
            onChange={(event) => {
              const nextLayer = event.target.value;
              setLayer(nextLayer);
              setStoredLayer(nextLayer);
            }}
          >
            <option value="gamma">Gamma</option>
            <option value="delta">Delta</option>
            <option value="volume">Volume</option>
          </select>
        </label>

        <label className="bmpro-select-control">
          <select
            value={activeTimeMode}
            disabled={!timeModesActionable}
            aria-label="Time window"
            onChange={(event) => {
              const nextMode = event.target.value;
              if (nextMode !== activeTimeMode) applyTimeWindowMode(nextMode);
            }}
          >
            {activeTimeMode === WINDOW_MODE.ABSOLUTE && (
              <option value={WINDOW_MODE.ABSOLUTE} disabled>Custom</option>
            )}
            <option value={WINDOW_MODE.SESSION}>Session</option>
            <option value={WINDOW_MODE.LATEST}>Latest</option>
          </select>
        </label>

        <span className="bmpro-chip bmpro-chip-market" title={viewLabel}>
          <span>{spotLabel}</span>
          <strong>{formatWholePrice(spot)}</strong>
        </span>

        {isEsSynthetic && (
          <label className="bmpro-chip bmpro-basis-chip">
            <span className="bmpro-chip-muted">Basis</span>
            <input
              type="number"
              step="0.25"
              value={basisDraft}
              onChange={(event) => setBasisDraft(event.target.value)}
              onBlur={commitBasisDraft}
              onKeyDown={handleBasisKeyDown}
              aria-label="Synthetic ES basis"
            />
          </label>
        )}

        {dots && <VolumeBubbleThresholdSelect className="bmpro-select-chip bmpro-select-chip-compact" />}

      </WidgetPanelControls>

      <div className="bmpro-grid">
        <div
          className="bmpro-main bm-lite-main"
          ref={mainWrapRef}
          style={{ '--bm-time-axis-height': `${BOOKMAP_TIME_AXIS_HEIGHT}px` }}
          onWheelCapture={handleWheel}
          onTouchStart={handleTouchZoom}
          onTouchMove={handleTouchZoom}
          onTouchEnd={handleTouchZoomEnd}
          onTouchCancel={handleTouchZoomEnd}
          onDragStart={(event) => event.preventDefault()}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerRelease}
          onPointerCancel={handlePointerRelease}
          onPointerLeave={(event) => {
            handlePointerRelease(event);
            useWidgetMinuteFocusStore.getState().clearFocusedMinute('flowmap');
          }}
          onDoubleClick={handleDoubleClick}
        >
          {ready ? (
            <>
              {hydrationMessage && (
                <div className="bmpro-hydration-status" role="status">{hydrationMessage}</div>
              )}
              <div ref={mainChartRef} className="bm-lite-chart bm-lite-main-chart" />
              {synchronizedCrosshairX != null && (
                <span
                  className="bm-lite-synchronized-crosshair"
                  style={{ left: synchronizedCrosshairX }}
                  aria-hidden="true"
                />
              )}
              {!primitives && <canvas ref={mainOverlayRef} className="bm-lite-overlay" />}
              {!primitives && <canvas ref={gexOverlayRef} className="bm-lite-gex-overlay" />}
              <canvas ref={axisOverlayRef} className="bm-lite-axis-overlay" />
              <div
                className="bm-lite-price-axis-hit-target"
                aria-hidden="true"
                style={{
                  width: BOOKMAP_STRIKE_GUTTER_WIDTH,
                  bottom: 0,
                }}
              />
              <div
                className="bm-lite-time-axis-hit-target"
                aria-hidden="true"
                style={{
                  left: BOOKMAP_STRIKE_GUTTER_WIDTH,
                  height: BOOKMAP_TIME_AXIS_HEIGHT,
                }}
              />
              {dots && !primitives && (
                <div className="bmpro-front-layer bm-lite-front-layer">
                  {displayedDots.map((dot) => (
                      <span
                        key={`${dot.t}-${dot.strike}-${Math.round(dot.dVol)}`}
                        className={`bmpro-volume-dot ${dot.climax ? 'is-climax' : ''} ${dot.deltaEstimated ? 'is-estimated' : ''} ${dot.recoveryGap ? 'is-recovery' : ''}`}
                        style={{
                          left: dot.cx - dot.r,
                          top: dot.cy - dot.r,
                          width: dot.r * 2,
                          height: dot.r * 2,
                          background: volumeDotGradient(dot),
                        }}
                      />
                  ))}
                </div>
              )}
              {(crosshairHover || bubbleHover) && (
                <div
                  ref={tipStackRef}
                  className="bmpro-tip-stack"
                  style={tipFollowStyle(
                    (crosshairHover?.tipX ?? bubbleHover?.cx) || 0,
                    (crosshairHover?.tipY ?? bubbleHover?.cy) || 0,
                    tipRegion,
                    tipSize
                  )}
                >
                  {zeroGexTimes.has(crosshairHover?.t ?? bubbleHover?.t) && (
                    <div className="bmpro-tip-card bmpro-tip-card--zerogex">
                      <strong className="bmpro-zerogex-label">0-GEX</strong>
                    </div>
                  )}
                  {bubbleHover && bubbleFirst && (
                    <div className="bmpro-tip-card bmpro-tip-card--bubble">
                      <span className="bmpro-tip-kicker">VOLUME BUBBLE</span>
                      <span>{bubbleHover.t ? etTime(bubbleHover.t) : '--'} / {spotLabel} VW Strike {Number.isFinite(bubbleHover.strike) ? Math.round(bubbleHover.strike) : '--'}</span>
                      <strong>Gross Vol {fmtCompact(bubbleHover.dVol)}</strong>
                      <span className={netToneClass(bubbleHover.netDelta)}>Flow Delta {fmtCompact(bubbleHover.netDelta)}</span>
                      {bubbleHover.deltaEstimated ? (
                        <span className="bmpro-est-note">Delta split unavailable from net delta</span>
                      ) : (
                        <>
                          <span><b className="bmpro-green">Bullish Delta {fmtCompact(bubbleHover.bullishDelta)}</b></span>
                          <span><b className="bmpro-red">Bearish Delta {fmtCompact(bubbleHover.bearishDelta)}</b></span>
                        </>
                      )}
                    </div>
                  )}
                  {crosshairHover?.kind === 'minute-volume'
                    && Number.isFinite(minuteSummaryClosePrice) && (
                    <div className="bmpro-tip-card">
                      <span className="bmpro-tip-kicker">MINUTE SUMMARY</span>
                      <span>{crosshairHover.t ? etTime(crosshairHover.t) : '--'} ET</span>
                      <span>Close {spotLabel} {fmtTooltipPrice0(minuteSummaryClosePrice)}</span>
                      {Number.isFinite(minuteSummaryGrossVolume) && (
                        <strong>Gross Vol {fmtCompact(minuteSummaryGrossVolume)}</strong>
                      )}
                      {Number.isFinite(minuteSummaryNetVolume) && (
                        <span className={netToneClass(minuteSummaryNetVolume)}>
                          Net Vol {fmtCompact(minuteSummaryNetVolume)}
                        </span>
                      )}
                    </div>
                  )}
                  {bubbleHover && !bubbleFirst && (
                    <div className="bmpro-tip-card bmpro-tip-card--bubble">
                      <span className="bmpro-tip-kicker">VOLUME BUBBLE</span>
                      <span>{bubbleHover.t ? etTime(bubbleHover.t) : '--'} / {spotLabel} VW Strike {Number.isFinite(bubbleHover.strike) ? Math.round(bubbleHover.strike) : '--'}</span>
                      <strong>Gross Vol {fmtCompact(bubbleHover.dVol)}</strong>
                      <span className={netToneClass(bubbleHover.netDelta)}>Flow Delta {fmtCompact(bubbleHover.netDelta)}</span>
                      {bubbleHover.deltaEstimated ? (
                        <span className="bmpro-est-note">Delta split unavailable from net delta</span>
                      ) : (
                        <>
                          <span><b className="bmpro-green">Bullish Delta {fmtCompact(bubbleHover.bullishDelta)}</b></span>
                          <span><b className="bmpro-red">Bearish Delta {fmtCompact(bubbleHover.bearishDelta)}</b></span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              <span className="bmpro-session-date">{sessionDateLabel}</span>
            </>
          ) : (
            <div className="bmpro-empty" role="status">{hydrationMessage || 'Loading...'}</div>
          )}
        </div>

        <div
          className="bmpro-hist"
          ref={histWrapRef}
          onWheelCapture={handleHistogramWheel}
          onPointerDown={handleHistogramPointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerRelease}
          onPointerCancel={handlePointerRelease}
          onDoubleClick={handleDoubleClick}
        >
          {ready ? <canvas ref={histCanvasRef} /> : <div className="bmpro-empty">{hydrationMessage || 'Loading...'}</div>}
        </div>

        <div className="bmpro-volume" ref={volumeWrapRef}>
          {ready ? (
            <div className="bmpro-volume-content">
              <div ref={volumeChartRef} className="bm-lite-chart bm-lite-volume-chart" />
              <canvas ref={volumeCanvasRef} className="bm-lite-volume-overlay" />
            </div>
          ) : <div className="bmpro-empty">{hydrationMessage || 'Loading...'}</div>}
        </div>

        <div className="bmpro-legend">
          <div className="bmpro-legend-title">FLOWMAP LEGEND</div>
          <div
            className={[
              'bmpro-swatch',
              palette === 'bookmap'
                ? 'bm-lite-swatch--bookmap'
                : 'bm-lite-swatch--pro',
              `bm-lite-swatch--${layer}`,
            ].join(' ')}
          >
            <div className="bmpro-swatch-scale">
              <span className="bmpro-swatch-scale-label bmpro-swatch-scale-label--left">
                {layerLegendLabel(layer)}
              </span>
              <span className="bmpro-swatch-scale-label bmpro-swatch-scale-label--center">
                0
              </span>
              <span className="bmpro-swatch-scale-label bmpro-swatch-scale-label--right">
                Intensity
              </span>
            </div>
          </div>
          {dots && (
            <>
              <div className="bmpro-legend-copy bmpro-legend-copy--balanced">Bubble: gross volume</div>
              <div className="bmpro-key-row bmpro-key-row--compact">
                <span className="bmpro-state-pair bmpro-state-pair--compact" aria-hidden="true">
                  <span className="bmpro-state-dot bmpro-state-dot--bullish" />
                  <span className="bmpro-state-dot bmpro-state-dot--bearish" />
                </span>
                <span className="bmpro-key-text bmpro-key-text--compact">delta: <b className="bmpro-green">bullish</b> / <b className="bmpro-red">bearish</b></span>
              </div>
            </>
          )}
          <div className="bmpro-key-row">
            {/*<span className="bmpro-zerogex-key" aria-hidden="true">*/}
            {/*  <span className="bmpro-zerogex-key-line" />*/}
            {/*  <span className="bmpro-zerogex-key-diamond" />*/}
            {/*</span>*/}
            {/*<span className="bmpro-key-text">0-GEX line to next cross</span>*/}
          </div>
        </div>
      </div>
    </div>
  );
}

export function BookmapLightweightPrimitives() {
  return <BookmapLightweightBase palette="bookmap" dots primitives />;
}
